import axios from "axios";
import { CSSProperties, PureComponent, ReactNode } from "react";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import { KuikaAppManager } from "../../../shared/utilty/kuika-app-manager";
import { CommonProps } from "../common-props";

// ── Types ────────────────────────────────────────────────────────────────────

interface ComparisonSummary {
  totalChanges: number;
  additions: number;
  deletions: number;
  modifications: number;
  unchangedCount: number;
  totalCount: number;
  similarityPercent: number;
}

interface FormatChange {
  location: string | null;
  property: string; // "bold" | "italic" | "underline" | "fontSize" | "fontFamily" | "color"
  oldValue: string | null;
  newValue: string | null;
  context: string | null;
}

interface ComparisonResult {
  documentId: string;
  documentName: string;
  mimeType: string;
  oldVersionId: string;
  newVersionId: string;
  oldVersion: number;
  newVersion: number;
  oldLanguage: string | null;
  newLanguage: string | null;
  comparisonMode:
    | "collabora"
    | "textDiff"
    | "cellDiff"
    | "slideDiff"
    | "pageDiff"
    | "pdfVisualDiff"
    | "scanPdfDiff"
    | "imageDiff"
    | "docxExport"
    | "unsupported"
    | "identical"
    | "error";
  summary: ComparisonSummary | null;
  chunks: DiffChunk[] | null;
  imageDiff: {
    oldWidth: number;
    oldHeight: number;
    newWidth: number;
    newHeight: number;
    similarityPercent: number;
    dimensionsMatch: boolean;
  } | null;
  formatChanges: FormatChange[] | null;
  message: string | null;
  scanDiff?: ScanDiffResult | null;
}

// Canonical-JSON-based diff result for scanPdfDiff mode.
// Mirrors Kuika.Common.Documents.Canonical.CanonicalDiffResult — keep the two in sync.
// Layout-invariant: page-level line sequence, block boundaries intentionally omitted.
interface ScanBBox {
  x: number;
  y: number;
  w: number;
  h: number;
}
interface ScanTokenDiff {
  type: "equal" | "insert" | "delete" | "modified";
  text: string;
  /** For "modified" tokens, the original (old-side) word text. */
  oldText?: string | null;
  /** Old-side bbox (populated for equal/delete/modified). */
  bbox?: ScanBBox | null;
  /** New-side bbox (populated for equal/insert/modified). */
  newBbox?: ScanBBox | null;
  isUncertain: boolean;
  /** Character-level diff segments, populated for "modified" word pairs. */
  charDiff?: ScanCharSegment[] | null;
}
interface ScanCharSegment {
  type: "equal" | "insert" | "delete";
  text: string;
}
interface ScanLineDiff {
  type: "unchanged" | "added" | "deleted" | "modified" | "moved";
  oldBbox?: ScanBBox | null;
  newBbox?: ScanBBox | null;
  oldText?: string | null;
  newText?: string | null;
  tokens?: ScanTokenDiff[] | null;
  isUncertain: boolean;
  similarity?: number | null;
}
interface ScanMovedGroup {
  oldBbox?: ScanBBox | null;
  newBbox?: ScanBBox | null;
  oldText: string;
  newText: string;
  similarity: number;
  lineCount: number;
}
// Presence of a non-text region (signature, stamp, handwriting, figure, chart, image, logo,
// formula) across the two revisions. Mirrors CanonicalDecorativeRegionPresence. The text diff
// deliberately skips these regions (their OCR is noise or asymmetric), so the UI consumes
// this separate list to draw bbox outlines + "added / removed / same / similar / different"
// badges.
interface ScanDecorativeRegionPresence {
  type: string;
  side: "old" | "new" | "both";
  oldBbox?: ScanBBox | null;
  newBbox?: ScanBBox | null;
  /** 64-bit perceptual hash Hamming distance. Populated only when side=="both" and both sides
   *  carry a pHash. Banding: ≤5 visually identical, 5-15 similar (re-render / recompression),
   *  >15 distinctly different. Null for one-sided presences. */
  pHashHammingDistance?: number | null;
  /** Multi-signal confidence [0, 1] from the Python pipeline (Phase 6 v2). UI styling bands:
   *  ≥0.70 high-confidence (solid red dashed border), 0.40-0.70 suspect (orange dashed +
   *  question-mark badge + tooltip). Regions below 0.40 are dropped server-side. */
  confidence?: number | null;
}
interface ScanPageDiff {
  oldPageNumber?: number | null;
  newPageNumber?: number | null;
  oldPageWidthPx?: number | null;
  oldPageHeightPx?: number | null;
  newPageWidthPx?: number | null;
  newPageHeightPx?: number | null;
  lines: ScanLineDiff[];
  movedGroups?: ScanMovedGroup[] | null;
  decorativeRegions?: ScanDecorativeRegionPresence[] | null;
}
interface ScanDiffSummary {
  additions: number;
  deletions: number;
  modifications: number;
  movedCount: number;
  uncertainCount: number;
  similarityPercent: number;
  /** Net added signatures (new-only − old-only) across all pages. */
  signatureDelta?: number;
  /** Net added decorative regions (signature + stamp + handwriting + figure + chart + image
   *  + logo + formula) across all pages. Non-zero even when the text diff is silent signals a
   *  structural change. */
  decorativeRegionsDelta?: number;
}
interface ScanDiffResult {
  schemaVersion: string;
  oldPageCount: number;
  newPageCount: number;
  oldSourceType: string;
  newSourceType: string;
  pages: ScanPageDiff[];
  summary: ScanDiffSummary;
}

interface DiffChunk {
  type: "unchanged" | "added" | "deleted" | "modified" | "separator";
  oldLineStart: number | null;
  newLineStart: number | null;
  content: string | null;
  oldContent: string | null;
}

interface CollaboraComparisonConfig {
  newVersionId: string;
  oldVersionId: string;
  oldVersionDownloadUrl: string;
  oldVersionAccessToken: string;
  oldVersionNumber: number;
  oldVersionLanguage: string | null;
  newVersionDocumentId: string;
  newVersionNumber: number;
  newVersionLanguage: string | null;
}

interface ProviderSettings {
  collabora: { enabled: boolean; serverUrl: string };
  onlyoffice: { enabled: boolean; documentServerUrl: string };
}

// ── Scan diff toolbar helpers ────────────────────────────────────────────────

const btnStyle = (disabled: boolean): CSSProperties => ({
  padding: "3px 8px",
  border: "1px solid #ccc",
  borderRadius: 3,
  background: disabled ? "#eee" : "#fff",
  color: disabled ? "#aaa" : "#333",
  cursor: disabled ? "default" : "pointer",
  fontSize: 12
});

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "#555" }}>
    <span style={{ width: 10, height: 10, background: color, borderRadius: 2, display: "inline-block" }} />
    {label}
  </span>
);

// ── Provider settings cache ──────────────────────────────────────────────────

let _cachedSettings: ProviderSettings | null = null;

async function fetchSettings(): Promise<ProviderSettings> {
  if (_cachedSettings) return _cachedSettings;
  try {
    const base = KuikaAppManager.GetBackendUrl().replace(/\/$/, "");
    const r = await axios.get(`${base}/documentmanagement/settings`, { timeout: 5_000 });
    _cachedSettings = {
      collabora: { enabled: !!r.data?.collabora?.enabled, serverUrl: r.data?.collabora?.serverUrl ?? "" },
      onlyoffice: {
        enabled: !!r.data?.onlyOffice?.enabled,
        documentServerUrl: r.data?.onlyOffice?.documentServerUrl ?? ""
      }
    };
  } catch {
    _cachedSettings = {
      collabora: { enabled: false, serverUrl: "" },
      onlyoffice: { enabled: false, documentServerUrl: "" }
    };
  }
  return _cachedSettings;
}

// ── Component ────────────────────────────────────────────────────────────────

interface DocumentCompareProps {
  /** KDocumentVersion.Id — left (old) version. Set via SetValueOf. */
  leftVersionId?: string;
  /** KDocumentVersion.Id — right (new) version. Set via SetValueOf. */
  rightVersionId?: string;
  /**
   * Internal: fired whenever an exposed output value (leftVersionId /
   * rightVersionId) changes. The code generator wires this to a host-screen
   * re-render so components binding directly to these values refresh.
   */
  onExposedValueChange?: () => void;
  /** DOCX/DOC/ODT engine: auto | collabora | diffplex */
  docxEngine?: "auto" | "collabora" | "diffplex";
  /** XLSX/XLS/ODS engine: auto | deep | diffplex */
  xlsxEngine?: "auto" | "deep" | "diffplex";
  /** PDF engine: auto | pdfpage | pdfvisual | diffplex */
  pdfEngine?: "auto" | "pdfpage" | "pdfvisual" | "diffplex";
  /** PPTX/PPT/ODP engine: auto | diffplex */
  pptxEngine?: "auto" | "diffplex";
  /** TXT/CSV/JSON/XML/HTML engine: auto | diffplex */
  textEngine?: "auto" | "diffplex";
  /** Scan-aware OCR compare: auto | scan | off. When "scan", forces OcrEnrichment pipeline for any PDF/DOCX input. */
  scanEngine?: "auto" | "scan" | "off";
  /** Container inline styles. */
  style?: CSSProperties;
  /** UI locale. Defaults to "tr". */
  locale?: "tr" | "en";
  /** Called when comparison finishes loading. */
  onLoad?: (result: ComparisonResult) => void;
  /** Called on error. */
  onError?: (error: Error) => void;
}

interface DocumentCompareState {
  loading: boolean;
  error: string | null;
  result: ComparisonResult | null;
  collaboraConfig: CollaboraComparisonConfig | null;
  collaboraIframeUrl: string | null;
  settings: ProviderSettings;
  collaboraComparisonSent: boolean;
  currentChangeIndex: number;
  oldImageBlobUrl: string | null;
  newImageBlobUrl: string | null;
  sidebarOpen: boolean;
  sidebarTab: "changes" | "format";
  exportingPdf: boolean;
  // scanPdfDiff canvas rendering
  scanLoading: boolean;
  scanError: string | null;
  scanPdfsLoaded: boolean;
  scanZoom: number;
  scanPageIdx: number; // 0-based
  /** Char-level diff popover (FAZ 6B). Set when the user hovers a "modified" token rect
   *  whose backend-supplied charDiff segments are populated. The popover renders the
   *  per-character delete/equal/insert breakdown so the user can see exactly which
   *  letters changed inside the word. */
  hoveredCharDiff: { tok: ScanTokenDiff; x: number; y: number } | null;
}

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

class DocumentCompareInner extends PureComponent<DocumentCompareProps & CommonProps, DocumentCompareState> {
  private _iframeRef: HTMLIFrameElement | null = null;
  private _collaboraSent = false;
  private _abortController: AbortController | null = null;
  private _collaboraTimeout: number | null = null;

  // FIX 1 & 5: Scroll sync flag and explicit panel refs
  private _isScrollSyncing = false;
  private _leftPanelRef: HTMLDivElement | null = null;
  private _rightPanelRef: HTMLDivElement | null = null;

  // scanPdfDiff rendering state (pdfjs-dist, lazy loaded)
  private _pdfjsLib: any = null;
  private _oldPdfDoc: any = null;
  private _newPdfDoc: any = null;
  private _scanLoadToken: number = 0;
  private _scanOldCanvasRef: HTMLCanvasElement | null = null;
  private _scanNewCanvasRef: HTMLCanvasElement | null = null;
  private _scanOldViewport: any = null;
  private _scanNewViewport: any = null;
  // In-flight pdfjs RenderTask handles — cancelled before starting a new render on same canvas.
  private _scanOldRenderTask: any = null;
  private _scanNewRenderTask: any = null;
  // Cross-side render-size normalization. Mixed-format compares (e.g. DOCX→PDF on the
  // left, native PDF on the right) often have different intrinsic page widths in pt:
  // Word's A4 ≈ 595, image-derived PDFs may be much wider/narrower. Without
  // normalization the same `scanZoom` produces visually different sizes per side.
  // We pick the larger of the two sides' page-1 native widths as a shared target and
  // upscale the smaller side to match — pdfjs renders vector content so upscaling is
  // free. `null` until both PDFs are loaded; per-page nativeW is measured at render
  // time so multi-page docs with mixed page sizes still align.
  private _scanGlobalTargetWidth: number | null = null;

  constructor(props: DocumentCompareProps & CommonProps) {
    super(props);
    this.state = {
      loading: false,
      error: null,
      result: null,
      collaboraConfig: null,
      collaboraIframeUrl: null,
      settings: _cachedSettings ?? {
        collabora: { enabled: false, serverUrl: "" },
        onlyoffice: { enabled: false, documentServerUrl: "" }
      },
      collaboraComparisonSent: false,
      currentChangeIndex: -1,
      oldImageBlobUrl: null,
      newImageBlobUrl: null,
      sidebarOpen: false,
      sidebarTab: "changes",
      exportingPdf: false,
      scanLoading: false,
      scanError: null,
      scanPdfsLoaded: false,
      scanZoom: 1.0,
      scanPageIdx: 0,
      hoveredCharDiff: null
    };
  }

  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _overrideLeftVersionId: string | null = null;
  private _overrideRightVersionId: string | null = null;

  async componentDidMount() {
    if ((window as any)?.kuika?.isDesignTime) return;
    const settings = await fetchSettings();
    this.setState({ settings });
    window.addEventListener("message", this.handlePostMessage);
    window.addEventListener("keydown", this.handleKeyDown);
    this.scheduleCompare();
  }

  componentDidUpdate(prev: DocumentCompareProps & CommonProps, prevState: DocumentCompareState) {
    if (prev.leftVersionId !== this.props.leftVersionId || prev.rightVersionId !== this.props.rightVersionId) {
      this._collaboraSent = false;
      this.scheduleCompare();
    }
    // First frame after PDFs finish loading — kick off the canvas render exactly once.
    // Ref callbacks no longer trigger renders themselves, so this is the single entry point
    // for the initial draw (subsequent redraws come from changeScanZoom / changeScanPage).
    if (!prevState.scanPdfsLoaded && this.state.scanPdfsLoaded) {
      const pageNum = this.state.scanPageIdx + 1;
      if (this._scanOldCanvasRef && this._oldPdfDoc) {
        void this.renderPdfPageToCanvas(this._oldPdfDoc, pageNum, this._scanOldCanvasRef, true);
      }
      if (this._scanNewCanvasRef && this._newPdfDoc) {
        void this.renderPdfPageToCanvas(this._newPdfDoc, pageNum, this._scanNewCanvasRef, false);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("message", this.handlePostMessage);
    window.removeEventListener("keydown", this.handleKeyDown);
    if (this._abortController) this._abortController.abort();
    if (this._collaboraTimeout) clearTimeout(this._collaboraTimeout);
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    // Revoke blob URLs
    if (this.state.oldImageBlobUrl) URL.revokeObjectURL(this.state.oldImageBlobUrl);
    if (this.state.newImageBlobUrl) URL.revokeObjectURL(this.state.newImageBlobUrl);
  }

  /** Debounced comparison trigger — waits 50ms for both left/right to be set before comparing. */
  private scheduleCompare() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(async () => {
      this._debounceTimer = null;
      // Ensure settings are loaded before comparing (setValueOf may fire before componentDidMount completes)
      if (!this.state.settings.collabora.enabled && !this.state.settings.onlyoffice.enabled) {
        const settings = await fetchSettings();
        this.setState({ settings });
      }
      const left = this._overrideLeftVersionId || this.props.leftVersionId;
      const right = this._overrideRightVersionId || this.props.rightVersionId;
      if (left && right && GUID_RE.test(left) && GUID_RE.test(right)) {
        this.loadComparison(left, right);
      }
    }, 50);
  }

  /** Called imperatively via setValueOf(componentName, versionId, "leftVersionId").
   *  When null is passed, clears the left side and resets the comparison. */
  public setLeftVersionId(versionId: string | null | undefined) {
    this._overrideLeftVersionId = versionId || null;
    this.props.onExposedValueChange?.();
    if (!versionId) {
      this.clearComparison();
      return;
    }
    this.scheduleCompare();
  }

  /** Called imperatively via setValueOf(componentName, versionId, "rightVersionId").
   *  When null is passed, clears the right side and resets the comparison. */
  public setRightVersionId(versionId: string | null | undefined) {
    this._overrideRightVersionId = versionId || null;
    this.props.onExposedValueChange?.();
    if (!versionId) {
      this.clearComparison();
      return;
    }
    this.scheduleCompare();
  }

  /** Resets the comparison to empty state. */
  private clearComparison() {
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    this.setState({
      result: null,
      error: null,
      loading: false,
      collaboraConfig: null,
      collaboraIframeUrl: null,
      collaboraComparisonSent: false,
      currentChangeIndex: 0,
      oldImageBlobUrl: null,
      newImageBlobUrl: null,
      exportingPdf: false
    });
  }

  // ── Public getters (symbol picker) ───────────────────────────────────────

  /** Getter for symbol picker — returns the current left version ID. */
  public getLeftVersionId(): string | null {
    return this._overrideLeftVersionId || this.props.leftVersionId || null;
  }

  /** Getter for symbol picker — returns the current right version ID. */
  public getRightVersionId(): string | null {
    return this._overrideRightVersionId || this.props.rightVersionId || null;
  }

  // ── Scroll sync ────────────────────────────────────────────────────────────

  private syncScroll = (source: HTMLDivElement, target: HTMLDivElement) => {
    if (this._isScrollSyncing) return;
    this._isScrollSyncing = true;
    target.scrollTop = source.scrollTop;
    requestAnimationFrame(() => {
      this._isScrollSyncing = false;
    });
  };

  // ── Keyboard shortcuts ──────────────────────────────────────────────────────

  private handleKeyDown = (e: KeyboardEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    if (e.key === "n" || e.key === "ArrowDown") {
      e.preventDefault();
      this.navigateChange(1);
    } else if (e.key === "p" || e.key === "ArrowUp") {
      e.preventDefault();
      this.navigateChange(-1);
    }
  };

  /** Returns indices of changed chunks in the current result */
  private getChangeIndices(): number[] {
    const { result } = this.state;
    if (!result?.chunks) return [];
    const indices: number[] = [];
    result.chunks.forEach((chunk, i) => {
      if (chunk.type !== "unchanged" && chunk.type !== "separator") {
        indices.push(i);
      }
    });
    return indices;
  }

  private navigateChange = (direction: 1 | -1) => {
    const changeIndices = this.getChangeIndices();
    if (changeIndices.length === 0) return;

    const { currentChangeIndex } = this.state;
    let newIndex: number;
    if (currentChangeIndex < 0) {
      newIndex = direction === 1 ? 0 : changeIndices.length - 1;
    } else {
      newIndex = currentChangeIndex + direction;
      if (newIndex < 0) newIndex = changeIndices.length - 1;
      if (newIndex >= changeIndices.length) newIndex = 0;
    }

    this.setState({ currentChangeIndex: newIndex });
    this.scrollToChange(changeIndices[newIndex]);
  };

  private scrollToChange = (chunkIndex: number) => {
    // Each line in the diff panels has a consistent height of 22px
    const lineHeight = 22;
    const headerHeight = 32; // panel header
    const scrollTarget = chunkIndex * lineHeight - headerHeight;

    if (this._leftPanelRef) {
      this._leftPanelRef.scrollTop = Math.max(0, scrollTarget);
    }
    if (this._rightPanelRef) {
      this._rightPanelRef.scrollTop = Math.max(0, scrollTarget);
    }
  };

  // ── Data loading ────────────────────────────────────────────────────────────

  private loadComparison = async (leftVersionId: string, rightVersionId: string) => {
    if (!GUID_RE.test(leftVersionId) || !GUID_RE.test(rightVersionId)) return;

    // Cancel previous comparison request
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    this._collaboraSent = false;
    if (this._collaboraTimeout) {
      clearTimeout(this._collaboraTimeout);
      this._collaboraTimeout = null;
    }
    // Revoke old blob URLs
    if (this.state.oldImageBlobUrl) URL.revokeObjectURL(this.state.oldImageBlobUrl);
    if (this.state.newImageBlobUrl) URL.revokeObjectURL(this.state.newImageBlobUrl);
    // Reset canvas-side state: previous rendered PDFs belong to the prior comparison.
    this.resetScanCanvasState();

    this.setState({
      loading: true,
      error: null,
      result: null,
      collaboraConfig: null,
      collaboraIframeUrl: null,
      collaboraComparisonSent: false,
      currentChangeIndex: -1,
      oldImageBlobUrl: null,
      newImageBlobUrl: null,
      sidebarOpen: false,
      exportingPdf: false
    });

    try {
      const base = KuikaAppManager.GetBackendUrl().replace(/\/$/, "");

      // Step 1: Get comparison result from backend
      // Don't compute text diff if Collabora will handle comparison (saves backend CPU)
      const wantsCollabora =
        (this.props.docxEngine || "auto") === "collabora" ||
        ((this.props.docxEngine || "auto") === "auto" && this.state.settings.collabora.enabled);
      const computeTextDiff = !wantsCollabora;
      const resp = await axios.post(
        `${base}/documentmanagement/compare`,
        {
          leftVersionId,
          rightVersionId,
          computeTextDiff,
          docxEngine: this.props.docxEngine || "auto",
          xlsxEngine: this.props.xlsxEngine || "auto",
          pdfEngine: this.props.pdfEngine || "auto",
          pptxEngine: this.props.pptxEngine || "auto",
          textEngine: this.props.textEngine || "auto",
          scanEngine: this.props.scanEngine || "auto"
        },
        { signal }
      );
      const result: ComparisonResult = resp.data;

      // Step 2a: If imageDiff, fetch both versions as blob URLs for preview
      if (result.comparisonMode === "imageDiff") {
        this.setState({ result, loading: false });
        this.props.onLoad?.(result);
        this.fetchImageBlobs(leftVersionId, rightVersionId, signal);
      }
      // Step 2b: If Collabora mode and Collabora is enabled, fetch WOPI config BEFORE setting loading=false
      else if (result.comparisonMode === "collabora" && this.state.settings.collabora.enabled) {
        const colResp = await axios.post(
          `${base}/documentmanagement/compare/collabora`,
          {
            leftVersionId,
            rightVersionId
          },
          { signal }
        );
        const colConfig: CollaboraComparisonConfig = colResp.data;

        // Get Collabora iframe URL for the NEW version.
        // Must be in EDIT mode — Action_CompareDocuments modifies the doc (inserts tracked changes).
        const wopiResp = await axios.post(
          `${base}/collabora/url`,
          {
            documentId: colConfig.newVersionDocumentId,
            versionId: colConfig.newVersionId,
            action: "edit"
          },
          { signal }
        );

        // Set everything together — result + collabora config + loading=false
        this.setState({
          result,
          loading: false,
          collaboraConfig: colConfig,
          collaboraIframeUrl: (() => {
            const rawUrl = wopiResp.data?.iframeUrl ?? wopiResp.data?.url ?? null;
            if (!rawUrl) return null;
            const origin = window.location.origin;
            const sep = rawUrl.includes("?") ? "&" : "?";
            const finalUrl = `${rawUrl}${sep}postmessage_origin=*`;

            return finalUrl;
          })()
        });
        this.props.onLoad?.(result);

        // Timeout: if Collabora doesn't send comparison within 30s, show error (no fallback)
        this._collaboraTimeout = window.setTimeout(() => {
          if (!this._collaboraSent) {
            this.setState({
              error:
                this.props.locale === "tr"
                  ? "Collabora sunucusu yanıt vermedi. Lütfen Collabora bağlantısını kontrol edin veya docxEngine='diffplex' kullanın."
                  : "Collabora server did not respond. Please check Collabora connection or use docxEngine='diffplex'.",
              loading: false,
              collaboraConfig: null,
              collaboraIframeUrl: null
            });
          }
        }, 30_000);
      }
      // Step 2c: All other modes — text diff, cell diff, slide diff, etc.
      else {
        this.setState({ result, loading: false });
        this.props.onLoad?.(result);
      }
    } catch (err: any) {
      if (axios.isCancel(err)) return; // previous request cancelled, ignore
      const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? "Comparison failed";
      this.setState({ loading: false, error: msg });
      this.props.onError?.(err instanceof Error ? err : new Error(msg));
    }
  };

  // ── Collabora PostMessage ───────────────────────────────────────────────────

  private handlePostMessage = (event: MessageEvent) => {
    if (!this.state.collaboraConfig) return;

    const serverUrl = this.state.settings.collabora.serverUrl;
    if (!serverUrl) return;
    if (!event.origin.startsWith(new URL(serverUrl).origin)) return;

    let data: any;
    try {
      data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }

    // Collabora loading failure → show error (no fallback to text diff)
    if (data?.MessageId === "App_LoadingStatus" && data?.Values?.Status === "Failed") {
      if (this._collaboraTimeout) {
        clearTimeout(this._collaboraTimeout);
        this._collaboraTimeout = null;
      }
      this.setState({
        error:
          this.props.locale === "tr"
            ? "Collabora dokümanı yükleyemedi. Lütfen bağlantıyı kontrol edin veya docxEngine='diffplex' kullanın."
            : "Collabora failed to load the document. Please check connection or use docxEngine='diffplex'.",
        collaboraConfig: null,
        collaboraIframeUrl: null
      });
      return;
    }

    // Collabora signals it's ready to receive PostMessages
    if (data?.MessageId === "Host_PostmessageReady") {
    }

    // When Collabora finishes loading, send the comparison command
    // Wait for BOTH Document_Loaded AND a small delay to ensure PostMessage handler is ready
    if (data?.MessageId === "App_LoadingStatus" && data?.Values?.Status === "Document_Loaded") {
      if (!this._collaboraSent && this._iframeRef?.contentWindow && this.state.collaboraConfig) {
        this._collaboraSent = true;
        if (this._collaboraTimeout) {
          clearTimeout(this._collaboraTimeout);
          this._collaboraTimeout = null;
        }
        this.setState({ collaboraComparisonSent: true });

        const collaboraOrigin = new URL(this.state.settings.collabora.serverUrl).origin;
        // Collabora Action_CompareDocuments handler expects:
        //   msg.Values.url (lowercase!) — URL to fetch the old document for comparison
        //   msg.Values.filename (optional) — display name for the old document
        // The URL must include access_token as query param (WOPI auth).
        const msg = {
          MessageId: "Action_CompareDocuments",
          SendTime: Date.now(),
          Values: {
            url: this.state.collaboraConfig.oldVersionDownloadUrl,
            filename: `v${this.state.collaboraConfig.oldVersionNumber ?? "old"}.docx`
          }
        };
        // Delay: Collabora may not be ready to receive PostMessages immediately after Document_Loaded
        setTimeout(() => {
          // Use "*" as target origin to bypass any origin mismatch issues
          this._iframeRef?.contentWindow?.postMessage(JSON.stringify(msg), "*");
        }, 2000);
      }
    }
  };

  private setIframeRef = (el: HTMLIFrameElement | null) => {
    this._iframeRef = el;
  };

  public setVersions(leftVersionId: string, rightVersionId: string) {
    this._collaboraSent = false;
    this._overrideLeftVersionId = leftVersionId;
    this._overrideRightVersionId = rightVersionId;
    this.scheduleCompare();
  }

  // ── Image blob fetcher ──────────────────────────────────────────────────────

  private fetchImageBlobs = async (leftVersionId: string, rightVersionId: string, signal: AbortSignal) => {
    try {
      const base = KuikaAppManager.GetBackendUrl().replace(/\/$/, "");
      const [oldResp, newResp] = await Promise.all([
        axios.get(`${base}/documentmanagement/version/${leftVersionId}/download`, {
          responseType: "blob",
          signal
        }),
        axios.get(`${base}/documentmanagement/version/${rightVersionId}/download`, {
          responseType: "blob",
          signal
        })
      ]);
      const oldUrl = URL.createObjectURL(oldResp.data);
      const newUrl = URL.createObjectURL(newResp.data);
      this.setState({ oldImageBlobUrl: oldUrl, newImageBlobUrl: newUrl });
    } catch {
      // Non-critical — image preview just won't show
    }
  };

  // ── PDF Export ──────────────────────────────────────────────────────────────

  private exportPdf = async () => {
    const left = this._overrideLeftVersionId || this.props.leftVersionId;
    const right = this._overrideRightVersionId || this.props.rightVersionId;
    if (!left || !right) return;

    this.setState({ exportingPdf: true });
    try {
      const base = KuikaAppManager.GetBackendUrl().replace(/\/$/, "");
      const resp = await axios.post(`${base}/documentmanagement/compare/as-file`, {
        leftVersionId: left,
        rightVersionId: right,
        computeTextDiff: true,
        docxEngine: this.props.docxEngine || "auto",
        xlsxEngine: this.props.xlsxEngine || "auto",
        pdfEngine: this.props.pdfEngine || "auto",
        pptxEngine: this.props.pptxEngine || "auto",
        textEngine: this.props.textEngine || "auto"
      });

      const { base64Content, mimeType, fileName } = resp.data;
      if (base64Content) {
        const byteChars = atob(base64Content);
        const byteArr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArr], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName || "comparison-report";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch {
      // Non-critical
    } finally {
      this.setState({ exportingPdf: false });
    }
  };

  // ── Render: Similarity badge ───────────────────────────────────────────────

  private renderSimilarityBadge() {
    const { result } = this.state;
    if (!result?.summary) return null;
    const sim = result.summary.similarityPercent ?? 100;
    const color = sim >= 90 ? "#28a745" : sim >= 60 ? "#ff9800" : "#d73a49";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "2px 8px",
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 700,
          color: "#fff",
          background: color
        }}
      >
        {sim.toFixed(1)}%
      </span>
    );
  }

  // ── Render: Statistics progress bar ────────────────────────────────────��───

  private renderStatsBar() {
    const { result } = this.state;
    if (!result?.summary || result.summary.totalCount === 0) return null;
    const s = result.summary;
    const total = s.totalCount || 1;
    const addPct = (s.additions / total) * 100;
    const delPct = (s.deletions / total) * 100;
    const modPct = (s.modifications / total) * 100;
    const unchPct = 100 - addPct - delPct - modPct;

    return (
      <div
        style={{
          display: "flex",
          width: 120,
          height: 6,
          borderRadius: 3,
          overflow: "hidden",
          background: "#eee"
        }}
        title={`${s.unchangedCount} unchanged, +${s.additions}, -${s.deletions}, ~${s.modifications}`}
      >
        {unchPct > 0 && <div style={{ width: `${unchPct}%`, background: "#c8e6c9" }} />}
        {addPct > 0 && <div style={{ width: `${addPct}%`, background: "#28a745" }} />}
        {modPct > 0 && <div style={{ width: `${modPct}%`, background: "#ff9800" }} />}
        {delPct > 0 && <div style={{ width: `${delPct}%`, background: "#d73a49" }} />}
      </div>
    );
  }

  // ── Render: Change list sidebar ────────────────────────────────────────────

  private renderSidebar() {
    const { result, sidebarOpen, sidebarTab, currentChangeIndex } = this.state;
    if (!sidebarOpen || !result) return null;
    const isTr = this.props.locale === "tr";

    const hasFormatChanges = result.formatChanges && result.formatChanges.length > 0;

    return (
      <div
        style={{
          width: 300,
          minWidth: 300,
          borderLeft: "1px solid #e0e0e0",
          display: "flex",
          flexDirection: "column",
          background: "#fafafa",
          fontFamily: "sans-serif",
          fontSize: 12,
          overflow: "hidden"
        }}
      >
        {/* Sidebar header with tabs */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
            background: "#f0f0f0"
          }}
        >
          <button
            type="button"
            onClick={() => this.setState({ sidebarTab: "changes" })}
            style={{
              flex: 1,
              padding: "8px 4px",
              border: "none",
              background: sidebarTab === "changes" ? "#fff" : "transparent",
              fontWeight: sidebarTab === "changes" ? 700 : 400,
              fontSize: 11,
              cursor: "pointer",
              borderBottom: sidebarTab === "changes" ? "2px solid #1a73e8" : "2px solid transparent"
            }}
          >
            {isTr ? "Degisiklikler" : "Changes"}
          </button>
          {hasFormatChanges && (
            <button
              type="button"
              onClick={() => this.setState({ sidebarTab: "format" })}
              style={{
                flex: 1,
                padding: "8px 4px",
                border: "none",
                background: sidebarTab === "format" ? "#fff" : "transparent",
                fontWeight: sidebarTab === "format" ? 700 : 400,
                fontSize: 11,
                cursor: "pointer",
                borderBottom: sidebarTab === "format" ? "2px solid #1a73e8" : "2px solid transparent"
              }}
            >
              {isTr ? "Format" : "Format"}
              <span
                style={{
                  marginLeft: 4,
                  padding: "1px 5px",
                  borderRadius: 8,
                  background: "#e0e0e0",
                  fontSize: 9,
                  fontWeight: 700
                }}
              >
                {result.formatChanges!.length}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={() => this.setState({ sidebarOpen: false })}
            style={{
              padding: "8px 10px",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: 14,
              color: "#888"
            }}
            title={isTr ? "Kapat" : "Close"}
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: "auto", padding: 0 }}>
          {sidebarTab === "changes" ? this.renderChangeList() : this.renderFormatChangeList()}
        </div>
      </div>
    );
  }

  private renderChangeList() {
    const { result, currentChangeIndex } = this.state;
    if (!result) return null;
    const isTr = this.props.locale === "tr";

    // Build change list from chunks
    const changes: { index: number; type: string; text: string; oldText?: string }[] = [];
    if (result.chunks) {
      result.chunks.forEach((chunk, i) => {
        if (chunk.type !== "unchanged" && chunk.type !== "separator") {
          changes.push({
            index: i,
            type: chunk.type,
            text: chunk.content || chunk.oldContent || "",
            oldText: chunk.oldContent || undefined
          });
        }
      });
    } else if ((result as any)?.cellChanges) {
      ((result as any).cellChanges as any[]).forEach((c, i) => {
        changes.push({
          index: i,
          type: c.type,
          text: `${c.cell}: ${c.newValue || c.oldValue || ""}`,
          oldText: c.oldValue
        });
      });
    } else if ((result as any)?.slideChanges) {
      ((result as any).slideChanges as any[]).forEach((s, i) => {
        changes.push({ index: i, type: s.type, text: `${isTr ? "Slayt" : "Slide"} ${s.slideNumber}` });
      });
    } else if ((result as any)?.pageChanges) {
      ((result as any).pageChanges as any[]).forEach((p, i) => {
        changes.push({ index: i, type: p.type, text: `${isTr ? "Sayfa" : "Page"} ${p.pageNumber}` });
      });
    }

    if (changes.length === 0) {
      return (
        <div style={{ padding: 16, color: "#888", textAlign: "center" }}>{isTr ? "Degisiklik yok" : "No changes"}</div>
      );
    }

    const typeIcon: Record<string, string> = { added: "+", deleted: "-", modified: "~" };
    const typeColor: Record<string, string> = { added: "#28a745", deleted: "#d73a49", modified: "#e36209" };
    const typeBg: Record<string, string> = { added: "#e6ffed", deleted: "#ffeef0", modified: "#fff8e1" };

    return (
      <>
        <div style={{ padding: "8px 12px", fontSize: 11, color: "#888", borderBottom: "1px solid #eee" }}>
          {changes.length} {isTr ? "degisiklik" : `change${changes.length !== 1 ? "s" : ""}`}
        </div>
        {changes.map((c, i) => {
          const isActive = currentChangeIndex === i;
          return (
            <div
              key={i}
              onClick={() => {
                this.setState({ currentChangeIndex: i });
                this.scrollToChange(c.index);
              }}
              style={{
                padding: "6px 12px",
                cursor: "pointer",
                background: isActive ? "#e3f2fd" : i % 2 === 0 ? "#fff" : "#fafafa",
                borderLeft: `3px solid ${typeColor[c.type] || "#888"}`,
                borderBottom: "1px solid #f0f0f0"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: typeBg[c.type] || "#eee",
                    color: typeColor[c.type] || "#888",
                    fontSize: 10,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  {typeIcon[c.type] || "?"}
                </span>
                <span style={{ fontSize: 10, color: typeColor[c.type] || "#888", fontWeight: 600 }}>
                  {c.type === "added"
                    ? isTr
                      ? "Eklenen"
                      : "Added"
                    : c.type === "deleted"
                      ? isTr
                        ? "Silinen"
                        : "Deleted"
                      : isTr
                        ? "Degisen"
                        : "Modified"}
                </span>
                <span style={{ fontSize: 10, color: "#aaa", marginLeft: "auto" }}>#{i + 1}</span>
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#333",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 260
                }}
              >
                {c.text.substring(0, 80)}
              </div>
            </div>
          );
        })}
      </>
    );
  }

  private renderFormatChangeList() {
    const { result } = this.state;
    if (!result?.formatChanges || result.formatChanges.length === 0) return null;
    const isTr = this.props.locale === "tr";

    const propLabel: Record<string, string> = {
      bold: isTr ? "Kalin" : "Bold",
      italic: isTr ? "Italik" : "Italic",
      underline: isTr ? "Alti Cizili" : "Underline",
      fontSize: isTr ? "Yazi Boyutu" : "Font Size",
      fontFamily: isTr ? "Yazi Tipi" : "Font Family",
      color: isTr ? "Renk" : "Color"
    };

    const propIcon: Record<string, string> = {
      bold: "B",
      italic: "I",
      underline: "U",
      fontSize: "Aa",
      fontFamily: "F",
      color: "#"
    };

    // Group by property
    const grouped: Record<string, FormatChange[]> = {};
    for (const fc of result.formatChanges) {
      if (!grouped[fc.property]) grouped[fc.property] = [];
      grouped[fc.property].push(fc);
    }

    return (
      <>
        <div style={{ padding: "8px 12px", fontSize: 11, color: "#888", borderBottom: "1px solid #eee" }}>
          {result.formatChanges.length} {isTr ? "format degisikligi" : "format changes"}
        </div>
        {Object.entries(grouped).map(([prop, changes]) => (
          <div key={prop}>
            <div
              style={{
                padding: "6px 12px",
                background: "#f0f0f0",
                fontSize: 11,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 6
              }}
            >
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 4,
                  background: "#e0e0e0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 700
                }}
              >
                {propIcon[prop] || "?"}
              </span>
              {propLabel[prop] || prop}
              <span style={{ marginLeft: "auto", fontSize: 10, color: "#888" }}>{changes.length}</span>
            </div>
            {changes.map((fc, i) => (
              <div key={i} style={{ padding: "4px 12px 4px 32px", borderBottom: "1px solid #f0f0f0", fontSize: 11 }}>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <span style={{ color: "#d73a49", textDecoration: "line-through" }}>{fc.oldValue}</span>
                  <span style={{ color: "#aaa" }}>&rarr;</span>
                  <span style={{ color: "#28a745", fontWeight: 600 }}>{fc.newValue}</span>
                </div>
                {fc.context && (
                  <div
                    style={{
                      fontSize: 10,
                      color: "#888",
                      marginTop: 1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    &ldquo;{fc.context}&rdquo;
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </>
    );
  }

  // ── Render: Design-time placeholder ─────────────────────────────────────────

  private renderPlaceholder() {
    return (
      <div
        className={this.props.className}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          minHeight: 120,
          background: "#f5f5f5",
          border: "1.5px dashed #bbb",
          borderRadius: 6,
          color: "#888",
          fontFamily: "sans-serif",
          pointerEvents: "none",
          userSelect: "none",
          boxSizing: "border-box",
          ...this.props.style
        }}
      >
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" style={{ marginBottom: 8, opacity: 0.45 }}>
          <path
            d="M9 2H4a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h6"
            stroke="#555"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M15 2h5a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2h-6"
            stroke="#555"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line x1="12" y1="2" x2="12" y2="22" stroke="#555" strokeWidth="1" strokeDasharray="3 3" />
        </svg>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Document Compare</span>
        <span style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
          {this.props.locale === "tr"
            ? "Karşılaştırmak için iki versiyon ID'si bağlayın"
            : "Bind two version IDs to compare"}
        </span>
      </div>
    );
  }

  // ── Render: Collabora tracked changes ───────────────────────────────────────

  // FIX 4: Loading overlay on top of Collabora iframe
  private renderCollabora() {
    const { collaboraIframeUrl } = this.state;
    return (
      <div style={{ position: "relative", width: "100%", height: "100%", minHeight: 800 }}>
        {!this.state.collaboraComparisonSent && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.85)",
              zIndex: 10,
              fontFamily: "sans-serif"
            }}
          >
            {this.props.locale === "tr" ? "Karşılaştırma yükleniyor..." : "Loading comparison..."}
          </div>
        )}
        {collaboraIframeUrl ? (
          <iframe
            ref={this.setIframeRef}
            src={collaboraIframeUrl}
            style={{ width: "100%", height: "100%", minHeight: 600, border: "none" }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-downloads"
            title="Document Comparison"
          />
        ) : (
          <div style={{ padding: 20, color: "#888" }}>
            {this.props.locale === "tr" ? "Hazırlanıyor..." : "Preparing comparison..."}
          </div>
        )}
      </div>
    );
  }

  // ── Render: LibreOffice compare (tracked changes DOCX in iframe) ─────────────

  private renderDocxExportCompare() {
    const { result, settings } = this.state;
    const resultUrl = (result as any)?.resultDocumentUrl;
    const isTr = this.props.locale === "tr";
    if (!resultUrl) {
      return (
        <div style={{ padding: 20, color: "#888", fontFamily: "sans-serif" }}>
          {isTr ? "Karsilastirma sonucu olusturulamadi." : "Comparison result could not be generated."}
        </div>
      );
    }

    // If OnlyOffice is available, show result DOCX in OnlyOffice iframe (read-only)
    if (settings.onlyoffice.enabled && settings.onlyoffice.documentServerUrl) {
      const dsUrl = settings.onlyoffice.documentServerUrl.replace(/\/$/, "");
      return (
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 600 }}>
          <div
            style={{
              padding: "6px 12px",
              background: "#e8f5e9",
              borderBottom: "1px solid #c8e6c9",
              fontSize: 12,
              fontFamily: "sans-serif",
              display: "flex",
              alignItems: "center",
              gap: 8
            }}
          >
            <span style={{ fontWeight: 600, color: "#2e7d32" }}>
              {isTr ? "Karsilastirma gorunumu (salt okunur)" : "Comparison view (read-only)"}
            </span>
            <span style={{ fontSize: 11, color: "#888" }}>
              <span style={{ color: "#008000", fontWeight: 700 }}>yesil bold</span> = {isTr ? "eklenen" : "added"},{" "}
              <span style={{ color: "#CC0000", fontStyle: "italic", textDecoration: "line-through" }}>
                kirmizi italic
              </span>{" "}
              = {isTr ? "silinen" : "deleted"}
            </span>
            <span style={{ flex: 1 }} />
          </div>
          <div
            id="onlyoffice-compare-editor"
            ref={(el) => {
              if (el && !(el as any).__ooInit) {
                (el as any).__ooInit = true;
                this.initOnlyOfficeViewer(el, dsUrl, resultUrl);
              }
            }}
            style={{ flex: 1, width: "100%" }}
          />
        </div>
      );
    }

    // If Collabora is available, show in Collabora iframe (read-only via WOPI)
    // For Collabora we'd need a WOPI endpoint for the result file — for now show download
    // with a note that OnlyOffice is recommended

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          minHeight: 400,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "sans-serif"
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 500 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#128196;</div>
          <h3 style={{ margin: "0 0 8px", color: "#333" }}>
            {isTr ? "Karsilastirma Dokumani Hazir" : "Comparison Document Ready"}
          </h3>
          <p style={{ color: "#666", fontSize: 14, lineHeight: 1.5, margin: "0 0 20px" }}>
            {isTr
              ? "Farklar Word Track Changes formatinda isaretlenmistir. Kirmizi ustu cizili = silinen, Yesil alti cizili = eklenen. Dokumani indirip Word, OnlyOffice veya Collabora ile acabilirsiniz."
              : "Differences are marked using Word Track Changes format. Red strikethrough = deleted, Green underline = added. Download and open with Word, OnlyOffice or Collabora."}
          </p>
          <a
            href={resultUrl}
            download="comparison-result.docx"
            style={{
              display: "inline-block",
              padding: "12px 32px",
              background: "#1a73e8",
              color: "#fff",
              borderRadius: 6,
              textDecoration: "none",
              fontSize: 15,
              fontWeight: 600
            }}
          >
            {isTr ? "Karsilastirma DOCX Indir" : "Download Comparison DOCX"}
          </a>
          {!settings.onlyoffice.enabled && (
            <p style={{ marginTop: 16, fontSize: 11, color: "#888" }}>
              {isTr
                ? "OnlyOffice Document Server etkinlestirildiginde, sonuc burada dogrudan goruntulenecektir."
                : "When OnlyOffice Document Server is enabled, results will be displayed inline here."}
            </p>
          )}
        </div>
      </div>
    );
  }

  private async initOnlyOfficeViewer(container: HTMLElement, dsUrl: string, docUrl: string) {
    const isTr = this.props.locale === "tr";

    // Step 1: Get signed config from backend (same pattern as Document element)
    // Extract just the file name from the full result URL (e.g., "abc123_result.docx")
    let fileName: string;
    try {
      const urlObj = new URL(docUrl, window.location.origin);
      const pathParts = urlObj.pathname.split("/");
      fileName = decodeURIComponent(pathParts[pathParts.length - 1]);
    } catch {
      fileName = docUrl.split("/").pop()?.split("?")[0] || docUrl;
    }

    let config: any;
    try {
      const base = KuikaAppManager.GetBackendUrl().replace(/\/$/, "");
      const resp = await axios.post(`${base}/documentmanagement/compare/lo-result/onlyoffice-config`, {
        fileName,
        title: "Comparison Result",
        locale: this.props.locale || "en"
      });
      config = resp.data;
    } catch (err: any) {
      console.error("[Compare] Failed to get OnlyOffice config:", err);
      // Fallback: show download button
      container.innerHTML = `<div style="padding:20px;text-align:center;color:#888;font-family:sans-serif;">
        <p>${isTr ? "OnlyOffice yapilandirmasi alinamadi." : "Could not get OnlyOffice config."}</p>
        <a href="${docUrl}" download="comparison-result.docx" style="color:#1a73e8;">${isTr ? "DOCX Indir" : "Download DOCX"}</a>
      </div>`;
      return;
    }

    // Step 2: Load OnlyOffice API script
    const scriptId = "onlyoffice-api-script";
    const serverUrl = config.documentServerUrl || dsUrl;
    const apiUrl = `${serverUrl}/web-apps/apps/api/documents/api.js`;

    const initEditor = () => {
      const DocsAPI = (window as any).DocsAPI;
      if (!DocsAPI) return;

      // Build final config using backend-signed values
      const editorConfig = {
        ...config,
        width: "100%",
        height: "100%",
        type: "embedded",
        events: {
          onReady: () => {
            // Editor ready
          },
          onError: (e: any) => {
            console.error("[Compare OnlyOffice] Error:", e);
          }
        }
      };

      new DocsAPI.DocEditor(container.id, editorConfig);
    };

    if ((window as any).DocsAPI) {
      initEditor();
    } else {
      let script = document.getElementById(scriptId) as HTMLScriptElement;
      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.src = apiUrl;
        script.onload = initEditor;
        document.head.appendChild(script);
      } else {
        // Script already loading — wait for it
        if ((window as any).DocsAPI) {
          initEditor();
        } else {
          script.addEventListener("load", initEditor);
        }
      }
    }
  }

  // ── Render: Text diff (side-by-side) ────────────────────────────────────────

  private renderTextDiff() {
    const { result } = this.state;
    if (!result?.chunks || result.chunks.length === 0) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            color: "#888",
            fontFamily: "sans-serif"
          }}
        >
          <span>
            {this.props.locale === "tr"
              ? "Karşılaştırılacak metin içeriği bulunamadı."
              : "No text content available for comparison."}
          </span>
        </div>
      );
    }

    const oldLines: {
      num: number | null;
      text: string;
      type: string;
      inline: { type: string; text: string }[] | null;
    }[] = [];
    const newLines: {
      num: number | null;
      text: string;
      type: string;
      inline: { type: string; text: string }[] | null;
    }[] = [];

    for (const chunk of result.chunks) {
      switch (chunk.type) {
        case "unchanged":
          oldLines.push({ num: chunk.oldLineStart, text: chunk.content ?? "", type: "unchanged", inline: null });
          newLines.push({ num: chunk.newLineStart, text: chunk.content ?? "", type: "unchanged", inline: null });
          break;
        case "added":
          oldLines.push({ num: null, text: "", type: "empty", inline: null });
          newLines.push({ num: chunk.newLineStart, text: chunk.content ?? "", type: "added", inline: null });
          break;
        case "deleted":
          oldLines.push({ num: chunk.oldLineStart, text: chunk.oldContent ?? "", type: "deleted", inline: null });
          newLines.push({ num: null, text: "", type: "empty", inline: null });
          break;
        case "modified": {
          // Word-level inline changes from backend
          const inl = (chunk as any).inlineChanges as { type: string; text: string }[] | null;
          if (inl && inl.length > 0) {
            // Old side: show equal + delete segments
            const oldInline = inl.filter((s) => s.type === "equal" || s.type === "delete");
            // New side: show equal + insert segments
            const newInline = inl.filter((s) => s.type === "equal" || s.type === "insert");
            oldLines.push({
              num: chunk.oldLineStart,
              text: chunk.oldContent ?? "",
              type: "modified",
              inline: oldInline
            });
            newLines.push({ num: chunk.newLineStart, text: chunk.content ?? "", type: "modified", inline: newInline });
          } else {
            oldLines.push({ num: chunk.oldLineStart, text: chunk.oldContent ?? "", type: "deleted", inline: null });
            newLines.push({ num: chunk.newLineStart, text: chunk.content ?? "", type: "added", inline: null });
          }
          break;
        }
        case "separator":
          oldLines.push({ num: null, text: "\u00b7\u00b7\u00b7", type: "separator", inline: null });
          newLines.push({ num: null, text: "\u00b7\u00b7\u00b7", type: "separator", inline: null });
          break;
      }
    }

    /** Renders word-level inline diff segments with highlighting */
    const renderInlineSegments = (segments: { type: string; text: string }[], side: "old" | "new") => {
      return segments.map((seg, i) => {
        if (seg.type === "equal") {
          return <span key={i}>{seg.text}</span>;
        }
        if (seg.type === "delete" && side === "old") {
          return (
            <span key={i} style={{ background: "#fdb8c0", textDecoration: "line-through", borderRadius: 2 }}>
              {seg.text}
            </span>
          );
        }
        if (seg.type === "insert" && side === "new") {
          return (
            <span key={i} style={{ background: "#acf2bd", fontWeight: 600, borderRadius: 2 }}>
              {seg.text}
            </span>
          );
        }
        return null;
      });
    };

    const lineStyle = (type: string): CSSProperties => ({
      padding: "1px 8px",
      fontFamily: "monospace",
      fontSize: 13,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      minHeight: 22,
      lineHeight: "22px",
      background:
        type === "added"
          ? "#e6ffed"
          : type === "deleted"
            ? "#ffeef0"
            : type === "modified"
              ? "#fff8e1"
              : type === "empty"
                ? "#fafafa"
                : type === "separator"
                  ? "#f0f0f0"
                  : "transparent",
      borderLeft:
        type === "added"
          ? "3px solid #28a745"
          : type === "deleted"
            ? "3px solid #d73a49"
            : type === "modified"
              ? "3px solid #ff9800"
              : "3px solid transparent"
    });

    const numStyle: CSSProperties = {
      width: 40,
      minWidth: 40,
      textAlign: "right",
      padding: "1px 6px",
      color: "#999",
      fontSize: 12,
      fontFamily: "monospace",
      userSelect: "none",
      lineHeight: "22px"
    };

    return (
      <div style={{ display: "flex", width: "100%", height: "100%", overflow: "hidden" }}>
        {/* Left panel — old version */}
        <div
          ref={(el) => {
            this._leftPanelRef = el;
          }}
          style={{ flex: 1, overflow: "auto", borderRight: "1px solid #ddd" }}
          onScroll={() => {
            if (this._leftPanelRef && this._rightPanelRef) {
              this.syncScroll(this._leftPanelRef, this._rightPanelRef);
            }
          }}
        >
          <div
            style={{
              padding: "8px 0",
              background: "#fafafa",
              borderBottom: "1px solid #eee",
              fontWeight: 600,
              fontSize: 13,
              paddingLeft: 12,
              color: "#666"
            }}
          >
            {(result as any).oldDocumentName || result!.documentName} — v{result!.oldVersion}
            {result!.oldLanguage ? ` (${result!.oldLanguage})` : ""}
          </div>
          {oldLines.map((line, i) => (
            <div key={i} style={{ display: "flex" }}>
              <div style={numStyle}>{line.num ?? ""}</div>
              <div style={{ ...lineStyle(line.type), flex: 1 }}>
                {line.inline ? renderInlineSegments(line.inline, "old") : line.text}
              </div>
            </div>
          ))}
        </div>

        {/* Right panel — new version */}
        <div
          ref={(el) => {
            this._rightPanelRef = el;
          }}
          style={{ flex: 1, overflow: "auto" }}
          onScroll={() => {
            if (this._rightPanelRef && this._leftPanelRef) {
              this.syncScroll(this._rightPanelRef, this._leftPanelRef);
            }
          }}
        >
          <div
            style={{
              padding: "8px 0",
              background: "#fafafa",
              borderBottom: "1px solid #eee",
              fontWeight: 600,
              fontSize: 13,
              paddingLeft: 12,
              color: "#666"
            }}
          >
            {result!.documentName} — v{result!.newVersion}
            {result!.newLanguage ? ` (${result!.newLanguage})` : ""}
          </div>
          {newLines.map((line, i) => (
            <div key={i} style={{ display: "flex" }}>
              <div style={numStyle}>{line.num ?? ""}</div>
              <div style={{ ...lineStyle(line.type), flex: 1 }}>
                {line.inline ? renderInlineSegments(line.inline, "new") : line.text}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Render: Summary bar ─────────────────────────────────────────────────────

  private renderSummary() {
    const { result, currentChangeIndex, sidebarOpen, exportingPdf } = this.state;
    if (!result) return null;
    const isTr = this.props.locale === "tr";

    // Show message bar even without summary (e.g., MIME mismatch info, error context)
    if (!result.summary) {
      if (result.message) {
        return (
          <div
            style={{
              padding: "8px 16px",
              background: "#fff3cd",
              borderBottom: "1px solid #ffc107",
              fontSize: 12,
              fontFamily: "sans-serif",
              color: "#856404"
            }}
          >
            {result.message}
          </div>
        );
      }
      return null;
    }
    const s = result.summary;
    const changeIndices = this.getChangeIndices();
    const hasChanges = changeIndices.length > 0;
    const hasFormatChanges = result.formatChanges && result.formatChanges.length > 0;

    // File type icon based on MIME
    const mimeIcon = this.getMimeIcon(result.mimeType);

    // Format-specific info
    const formatInfo = this.getFormatSpecificInfo(result);

    const navBtnStyle: CSSProperties = {
      padding: "2px 10px",
      fontSize: 11,
      fontWeight: 600,
      border: "1px solid #ccc",
      borderRadius: 4,
      background: "#fff",
      cursor: hasChanges ? "pointer" : "not-allowed",
      opacity: hasChanges ? 1 : 0.4,
      fontFamily: "sans-serif",
      color: "#555"
    };

    const toolBtnStyle: CSSProperties = {
      padding: "2px 8px",
      fontSize: 11,
      fontWeight: 500,
      border: "1px solid #ccc",
      borderRadius: 4,
      background: "#fff",
      cursor: "pointer",
      fontFamily: "sans-serif",
      color: "#555"
    };

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 16px",
          background: "#f8f9fa",
          borderBottom: "1px solid #e0e0e0",
          fontSize: 13,
          fontFamily: "sans-serif",
          color: "#555",
          flexWrap: "wrap"
        }}
      >
        {/* File icon and document names */}
        <span style={{ fontSize: 16, lineHeight: 1 }}>{mimeIcon}</span>
        <span style={{ fontSize: 12, color: "#888" }}>
          {(result as any).oldDocumentName || result.documentName} v{result.oldVersion}
          {result.oldLanguage ? ` (${result.oldLanguage})` : ""}
        </span>
        <span style={{ color: "#aaa", fontSize: 14, fontWeight: 700 }}>&rarr;</span>
        <span style={{ fontSize: 12, color: "#333", fontWeight: 600 }}>
          {result.documentName} v{result.newVersion}
          {result.newLanguage ? ` (${result.newLanguage})` : ""}
        </span>

        <span style={{ color: "#ddd" }}>|</span>

        {/* Similarity badge */}
        {result.comparisonMode !== "collabora" && this.renderSimilarityBadge()}

        {/* Statistics mini progress bar */}
        {result.comparisonMode !== "collabora" && this.renderStatsBar()}

        {/* Change counts */}
        <span style={{ fontSize: 12 }}>
          {result.comparisonMode === "collabora"
            ? isTr
              ? "Degisiklikler asagida gosterilmektedir"
              : "Changes shown below"
            : `${s.totalChanges} ${isTr ? "degisiklik" : `change${s.totalChanges !== 1 ? "s" : ""}`}`}
        </span>
        {result.comparisonMode !== "collabora" && s.additions > 0 && (
          <span style={{ color: "#28a745", fontSize: 12 }}>+{s.additions}</span>
        )}
        {result.comparisonMode !== "collabora" && s.deletions > 0 && (
          <span style={{ color: "#d73a49", fontSize: 12 }}>-{s.deletions}</span>
        )}
        {result.comparisonMode !== "collabora" && s.modifications > 0 && (
          <span style={{ color: "#e36209", fontSize: 12 }}>~{s.modifications}</span>
        )}

        {/* Format changes badge */}
        {hasFormatChanges && (
          <span
            style={{
              padding: "1px 6px",
              borderRadius: 8,
              background: "#e8eaf6",
              color: "#3f51b5",
              fontSize: 10,
              fontWeight: 600,
              cursor: "pointer"
            }}
            onClick={() => this.setState({ sidebarOpen: true, sidebarTab: "format" })}
            title={isTr ? "Format degisiklikleri" : "Format changes"}
          >
            {result.formatChanges!.length} {isTr ? "format" : "format"}
          </span>
        )}

        {/* Format-specific info */}
        {formatInfo && (
          <>
            <span style={{ color: "#ddd" }}>|</span>
            <span style={{ fontSize: 11, color: "#888" }}>{formatInfo}</span>
          </>
        )}

        {/* Spacer */}
        <span style={{ flex: 1 }} />

        {/* Navigation: prev/next change */}
        {hasChanges && (
          <>
            <button
              type="button"
              style={navBtnStyle}
              onClick={() => this.navigateChange(-1)}
              title={isTr ? "Onceki degisiklik (Ctrl+P)" : "Previous change (Ctrl+P)"}
            >
              &#9650;
            </button>
            <span style={{ fontSize: 11, color: "#888", minWidth: 36, textAlign: "center" }}>
              {currentChangeIndex >= 0
                ? `${currentChangeIndex + 1}/${changeIndices.length}`
                : `—/${changeIndices.length}`}
            </span>
            <button
              type="button"
              style={navBtnStyle}
              onClick={() => this.navigateChange(1)}
              title={isTr ? "Sonraki degisiklik (Ctrl+N)" : "Next change (Ctrl+N)"}
            >
              &#9660;
            </button>
          </>
        )}

        {/* DOCX download — only for docxExport mode */}
        {result.comparisonMode === "docxExport" && (result as any)?.resultDocumentUrl && (
          <a
            href={(result as any).resultDocumentUrl}
            download="comparison-result.docx"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 10px",
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid #c8e6c9",
              borderRadius: 4,
              background: "#e8f5e9",
              color: "#2e7d32",
              textDecoration: "none",
              cursor: "pointer"
            }}
            title={isTr ? "Karsilastirma DOCX indir" : "Download comparison DOCX"}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#2e7d32"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            DOCX
          </a>
        )}
      </div>
    );
  }

  /** Returns an appropriate file icon character for the MIME type. */
  private getMimeIcon(mimeType: string): string {
    if (!mimeType) return "\uD83D\uDCC4"; // generic file
    if (mimeType.startsWith("image/")) return "\uD83D\uDDBC"; // framed picture
    if (mimeType.includes("pdf")) return "\uD83D\uDCC4"; // page
    if (mimeType.includes("spreadsheet") || mimeType.includes("excel")) return "\uD83D\uDCCA"; // bar chart
    if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return "\uD83D\uDCFD"; // projector
    if (mimeType.includes("word") || mimeType.includes("document")) return "\uD83D\uDCC3"; // page with curl
    return "\uD83D\uDCC4";
  }

  /** Returns format-specific summary text based on comparison mode. */
  private getFormatSpecificInfo(result: ComparisonResult): string | null {
    const isTr = this.props.locale === "tr";
    const s = result.summary;
    if (!s) return null;

    switch (result.comparisonMode) {
      case "cellDiff": {
        const cells = (result as any)?.cellChanges?.length ?? s.totalChanges;
        return isTr ? `${cells} hücre değişti` : `${cells} cell${cells !== 1 ? "s" : ""} changed`;
      }
      case "pageDiff": {
        const pages = (result as any)?.pageChanges ?? [];
        const modifiedPages = pages.filter((p: any) => p.type === "modified").map((p: any) => p.pageNumber);
        if (modifiedPages.length > 0 && modifiedPages.length <= 5) {
          const pageList = modifiedPages.join(", ");
          return isTr
            ? `Sayfa ${pageList} değişti`
            : `Page${modifiedPages.length > 1 ? "s" : ""} ${pageList} differ${modifiedPages.length === 1 ? "s" : ""}`;
        }
        return isTr
          ? `${pages.length} sayfa etkilendi`
          : `${pages.length} page${pages.length !== 1 ? "s" : ""} affected`;
      }
      case "slideDiff": {
        const slides = (result as any)?.slideChanges ?? [];
        return isTr
          ? `${slides.length} slayt etkilendi`
          : `${slides.length} slide${slides.length !== 1 ? "s" : ""} affected`;
      }
      case "imageDiff": {
        const img = result.imageDiff;
        if (img) {
          const sim = `${img.similarityPercent.toFixed(1)}%`;
          return isTr ? `Benzerlik: ${sim}` : `Similarity: ${sim}`;
        }
        return null;
      }
      default:
        return null;
    }
  }

  // ── Render: Identical versions ──────────────────────────────────────────────

  private renderIdentical() {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          color: "#28a745",
          fontFamily: "sans-serif"
        }}
      >
        <span style={{ fontSize: 48, marginBottom: 16 }}>&#10003;</span>
        <span style={{ fontSize: 15, fontWeight: 600 }}>
          {this.props.locale === "tr" ? "Fark bulunamadı" : "No differences"}
        </span>
        <span style={{ fontSize: 13, color: "#888", marginTop: 8 }}>
          {this.state.result?.message ??
            (this.props.locale === "tr" ? "Seçilen versiyonlar aynı." : "The selected versions are identical.")}
        </span>
      </div>
    );
  }

  // ── Render: PDF Page diff ────────────────────────────────────────────────

  private renderPageDiff() {
    const { result } = this.state;
    const pages: any[] = (result as any)?.pageChanges ?? [];
    const isTr = this.props.locale === "tr";

    if (pages.length === 0) return this.renderIdentical();

    const typeColor: Record<string, string> = { added: "#e6ffed", deleted: "#ffeef0", modified: "#fff8e1" };
    const typeBadge: Record<string, string> = { added: "#28a745", deleted: "#d73a49", modified: "#e36209" };
    const typeLabel: Record<string, string> = {
      added: isTr ? "Yeni Sayfa" : "New Page",
      deleted: isTr ? "Silinen Sayfa" : "Deleted Page",
      modified: isTr ? "Değişen Sayfa" : "Modified Page"
    };

    const lineStyle = (type: string): CSSProperties => ({
      padding: "1px 6px",
      fontFamily: "monospace",
      fontSize: 11,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      minHeight: 18,
      lineHeight: "18px",
      background: type === "added" ? "#e6ffed" : type === "deleted" ? "#ffeef0" : "transparent",
      borderLeft:
        type === "added" ? "2px solid #28a745" : type === "deleted" ? "2px solid #d73a49" : "2px solid transparent"
    });

    return (
      <div style={{ width: "100%", height: "100%", overflow: "auto", fontFamily: "sans-serif", fontSize: 12 }}>
        {pages.map((p: any, i: number) => (
          <div key={i} style={{ marginBottom: 8, border: "1px solid #e0e0e0", borderRadius: 8, overflow: "hidden" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                background: typeColor[p.type] || "#f8f9fa",
                borderBottom: "1px solid #e0e0e0"
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 13 }}>
                {isTr ? "Sayfa" : "Page"} {p.pageNumber}
              </span>
              <span
                style={{
                  display: "inline-block",
                  padding: "1px 8px",
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#fff",
                  background: typeBadge[p.type] || "#888"
                }}
              >
                {typeLabel[p.type] || p.type}
              </span>
              {p.summary && (
                <span style={{ fontSize: 10, color: "#888" }}>
                  +{p.summary.additions} -{p.summary.deletions} ~{p.summary.modifications}
                </span>
              )}
            </div>
            {p.type === "modified" && p.chunks?.length > 0 ? (
              <div style={{ maxHeight: 200, overflow: "auto", padding: 4 }}>
                {p.chunks.map((chunk: any, ci: number) => (
                  <div key={ci} style={lineStyle(chunk.type)}>
                    {chunk.type === "deleted" ? chunk.oldContent : chunk.content}
                  </div>
                ))}
              </div>
            ) : p.type === "added" ? (
              <div style={{ padding: "8px 12px", fontSize: 11, color: "#28a745" }}>{p.newText}</div>
            ) : p.type === "deleted" ? (
              <div style={{ padding: "8px 12px", fontSize: 11, color: "#d73a49", textDecoration: "line-through" }}>
                {p.oldText}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    );
  }

  // ── Render: PDF Visual diff (diff-pdf output) ──────────────────────────

  private renderPdfVisualDiff() {
    const { result } = this.state;
    const diffBase64 = (result as any)?.diffPdfBase64;
    const isTr = this.props.locale === "tr";

    if (!diffBase64) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            color: "#888"
          }}
        >
          {isTr ? "Görsel diff oluşturulamadı." : "Visual diff could not be generated."}
        </div>
      );
    }

    const pdfUrl = `data:application/pdf;base64,${diffBase64}`;

    return (
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
        <div
          style={{
            padding: "8px 12px",
            background: "#fff3cd",
            borderBottom: "1px solid #ffc107",
            fontSize: 12,
            color: "#856404"
          }}
        >
          {isTr
            ? "Kırmızı alanlar değişiklikleri gösterir (diff-pdf pixel karşılaştırması)."
            : "Red areas indicate changes (diff-pdf pixel comparison)."}
        </div>
        <iframe src={pdfUrl} style={{ flex: 1, border: "none", width: "100%" }} title="PDF Visual Diff" />
      </div>
    );
  }

  // ── Scan-aware compare: pdfjs canvas + bbox overlay (Diffchecker-style) ─

  private async loadPdfjs(): Promise<any> {
    if (this._pdfjsLib) return this._pdfjsLib;
    // `pdfjs-dist` main build uses `Map.prototype.getOrInsertComputed` (TC39 Stage 3, not
    // shipped in any browser yet → "getOrInsertComputed is not a function"). The legacy
    // build targets ES2017 + ships polyfills, so it runs everywhere.
    const pdfjs: any = await import("pdfjs-dist/legacy/build/pdf.mjs");
    const cdn = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;
    pdfjs.GlobalWorkerOptions.workerSrc = cdn;
    // eslint-disable-next-line no-console
    console.info("[DocumentCompare] pdfjs-dist (legacy)", pdfjs.version, "workerSrc=", cdn);
    this._pdfjsLib = pdfjs;
    return pdfjs;
  }

  private resetScanCanvasState = () => {
    this._oldPdfDoc = null;
    this._newPdfDoc = null;
    this._scanOldCanvasRef = null;
    this._scanNewCanvasRef = null;
    this._scanOldViewport = null;
    this._scanNewViewport = null;
    this._scanGlobalTargetWidth = null;
    this.setState({ scanPdfsLoaded: false, scanLoading: false, scanError: null, scanPageIdx: 0 });
  };

  private loadScanPdfs = async () => {
    const { result } = this.state;
    if (result?.comparisonMode !== "scanPdfDiff") return;
    const leftId = this._overrideLeftVersionId ?? this.props.leftVersionId;
    const rightId = this._overrideRightVersionId ?? this.props.rightVersionId;
    if (!leftId || !rightId) return;

    const myToken = ++this._scanLoadToken;
    this.setState({ scanLoading: true, scanError: null });

    try {
      const pdfjs = await this.loadPdfjs();
      const base = KuikaAppManager.GetBackendUrl().replace(/\/$/, "");
      const [oldResp, newResp] = await Promise.all([
        axios.get(`${base}/documentmanagement/compare/render/${leftId}`, { responseType: "arraybuffer" }),
        axios.get(`${base}/documentmanagement/compare/render/${rightId}`, { responseType: "arraybuffer" })
      ]);
      if (myToken !== this._scanLoadToken) return; // newer load superseded us

      // eslint-disable-next-line no-console
      console.info("[DocumentCompare] rendered pdf bytes:", oldResp.data?.byteLength, "/", newResp.data?.byteLength);

      const [oldDoc, newDoc] = await Promise.all([
        pdfjs.getDocument({ data: new Uint8Array(oldResp.data) }).promise,
        pdfjs.getDocument({ data: new Uint8Array(newResp.data) }).promise
      ]);
      if (myToken !== this._scanLoadToken) return;

      // eslint-disable-next-line no-console
      console.info("[DocumentCompare] pdfjs docs loaded — pages:", oldDoc.numPages, "/", newDoc.numPages);

      this._oldPdfDoc = oldDoc;
      this._newPdfDoc = newDoc;

      // Measure page-1 native widths so we can normalize both sides to the larger one.
      // Without this, a DOCX→LibreOffice→PDF (A4 ≈ 595pt) renders visibly smaller than
      // a native PDF whose intrinsic width is bigger, even at the same `scanZoom`.
      try {
        const [oldP1, newP1] = await Promise.all([oldDoc.getPage(1), newDoc.getPage(1)]);
        if (myToken !== this._scanLoadToken) return;
        const oldNativeW = oldP1.getViewport({ scale: 1 }).width;
        const newNativeW = newP1.getViewport({ scale: 1 }).width;
        this._scanGlobalTargetWidth = Math.max(oldNativeW, newNativeW);
        // eslint-disable-next-line no-console
        console.info(
          "[DocumentCompare] page-1 native widths (pt): old=",
          oldNativeW,
          "new=",
          newNativeW,
          "→ targetW=",
          this._scanGlobalTargetWidth
        );
      } catch (e) {
        // Non-fatal: fall back to no normalization (previous behavior).
        // eslint-disable-next-line no-console
        console.warn("[DocumentCompare] could not measure native widths, skipping normalization:", e);
        this._scanGlobalTargetWidth = null;
      }

      // Initial render is triggered from componentDidUpdate on the false → true transition
      // of scanPdfsLoaded — a single deterministic tetik, no race with ref callbacks.
      this.setState({ scanPdfsLoaded: true, scanLoading: false, scanPageIdx: 0 });
    } catch (e: any) {
      if (myToken !== this._scanLoadToken) return;
      // eslint-disable-next-line no-console
      console.error("[DocumentCompare] loadScanPdfs failed:", e);
      const msg = e?.response?.data?.message ?? e?.message ?? String(e);
      this.setState({ scanLoading: false, scanError: msg });
    }
  };

  private renderPdfPageToCanvas = async (pdfDoc: any, pageNum: number, canvas: HTMLCanvasElement, isOld: boolean) => {
    if (!pdfDoc || !canvas) return;

    // Serialize renders on the same canvas. pdfjs v5 throws
    // "Cannot use the same canvas during multiple render() operations" otherwise.
    const existing = isOld ? this._scanOldRenderTask : this._scanNewRenderTask;
    if (existing) {
      try {
        existing.cancel();
      } catch {
        /* ignore */
      }
      try {
        await existing.promise;
      } catch {
        /* cancellation rejects, that's expected */
      }
    }

    try {
      const page = await pdfDoc.getPage(pageNum);
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      // Per-page normalization: bring this side's page width up to the global target
      // (= max of both sides' page-1 native widths). Computed per-page so multi-page
      // docs with mixed page sizes (e.g. A4 + A3) still align side-by-side.
      const nativeW = page.getViewport({ scale: 1 }).width;
      const targetW = this._scanGlobalTargetWidth;
      const normScale = targetW && nativeW > 0 ? targetW / nativeW : 1;
      const scale = this.state.scanZoom * dpr * normScale;
      const viewport = page.getViewport({ scale });
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${viewport.width / dpr}px`;
      canvas.style.height = `${viewport.height / dpr}px`;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        // eslint-disable-next-line no-console
        console.error("[DocumentCompare] canvas 2d context unavailable");
        return;
      }
      // eslint-disable-next-line no-console
      console.info(
        "[DocumentCompare] rendering page",
        pageNum,
        "isOld=",
        isOld,
        "nativeW=",
        nativeW,
        "normScale=",
        normScale.toFixed(3),
        "viewport=",
        viewport.width,
        "x",
        viewport.height
      );

      const task = page.render({ canvasContext: ctx, viewport });
      if (isOld) this._scanOldRenderTask = task;
      else this._scanNewRenderTask = task;
      try {
        await task.promise;
      } finally {
        if (isOld && this._scanOldRenderTask === task) this._scanOldRenderTask = null;
        if (!isOld && this._scanNewRenderTask === task) this._scanNewRenderTask = null;
      }

      if (isOld) this._scanOldViewport = viewport;
      else this._scanNewViewport = viewport;
      // Overlay recalc — safe now that tasks are serialized.
      this.forceUpdate();
    } catch (e: any) {
      // Cancellation is expected when zoom/page changes mid-render.
      if (e?.name === "RenderingCancelledException") return;
      // eslint-disable-next-line no-console
      console.error("[DocumentCompare] pdfjs render page failed:", e);
      this.setState({ scanError: `PDF render failed: ${e?.message ?? e}` });
    }
  };

  private onScanOldCanvasRef = (c: HTMLCanvasElement | null) => {
    // Ref saklamak — render'ı buradan tetikleme. Conditional remount sırasında ref callback
    // tekrar çağrılabilir, o anda zaten in-flight bir render varsa çakışma olur. Explicit
    // render'lar componentDidUpdate (ilk mount) + changeScanZoom/changeScanPage yolundan gelir.
    this._scanOldCanvasRef = c;
  };

  private onScanNewCanvasRef = (c: HTMLCanvasElement | null) => {
    this._scanNewCanvasRef = c;
  };

  private onScanLeftScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (this._isScrollSyncing) return;
    const src = e.currentTarget;
    const dst = this._rightPanelRef;
    if (!dst) return;
    this._isScrollSyncing = true;
    dst.scrollTop = src.scrollTop;
    dst.scrollLeft = src.scrollLeft;
    requestAnimationFrame(() => {
      this._isScrollSyncing = false;
    });
  };

  private onScanRightScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (this._isScrollSyncing) return;
    const src = e.currentTarget;
    const dst = this._leftPanelRef;
    if (!dst) return;
    this._isScrollSyncing = true;
    dst.scrollTop = src.scrollTop;
    dst.scrollLeft = src.scrollLeft;
    requestAnimationFrame(() => {
      this._isScrollSyncing = false;
    });
  };

  /** FAZ 6B — char-level diff popover handlers. Show the popover when the user hovers
   *  a "modified" token rect that has charDiff segments populated by the backend.
   *  Hide when the cursor leaves. The popover follows the mouse so it doesn't get
   *  clipped against the panel edges. */
  private onModifiedTokenEnter = (tok: ScanTokenDiff, e: React.MouseEvent) => {
    if (!tok.charDiff || tok.charDiff.length === 0) return;
    this.setState({ hoveredCharDiff: { tok, x: e.clientX, y: e.clientY } });
  };

  private onModifiedTokenMove = (e: React.MouseEvent) => {
    const cur = this.state.hoveredCharDiff;
    if (!cur) return;
    // Update position so the popover tracks the cursor; cheap state update because
    // PureComponent only re-renders the popover branch, not the entire SVG.
    this.setState({ hoveredCharDiff: { tok: cur.tok, x: e.clientX, y: e.clientY } });
  };

  private onModifiedTokenLeave = () => {
    if (this.state.hoveredCharDiff) {
      this.setState({ hoveredCharDiff: null });
    }
  };

  private changeScanZoom = (delta: number) => {
    const next = Math.max(0.4, Math.min(3.0, +(this.state.scanZoom + delta).toFixed(2)));
    this.setState({ scanZoom: next }, () => {
      if (this._scanOldCanvasRef && this._oldPdfDoc) {
        void this.renderPdfPageToCanvas(this._oldPdfDoc, this.state.scanPageIdx + 1, this._scanOldCanvasRef, true);
      }
      if (this._scanNewCanvasRef && this._newPdfDoc) {
        void this.renderPdfPageToCanvas(this._newPdfDoc, this.state.scanPageIdx + 1, this._scanNewCanvasRef, false);
      }
    });
  };

  private changeScanPage = (delta: number) => {
    const pageCount = this.getScanPageCount();
    const next = Math.max(0, Math.min(pageCount - 1, this.state.scanPageIdx + delta));
    if (next === this.state.scanPageIdx) return;
    this.setState({ scanPageIdx: next }, () => {
      if (this._scanOldCanvasRef && this._oldPdfDoc) {
        void this.renderPdfPageToCanvas(this._oldPdfDoc, next + 1, this._scanOldCanvasRef, true);
      }
      if (this._scanNewCanvasRef && this._newPdfDoc) {
        void this.renderPdfPageToCanvas(this._newPdfDoc, next + 1, this._scanNewCanvasRef, false);
      }
    });
  };

  private getScanPageCount(): number {
    const o = this._oldPdfDoc?.numPages ?? 0;
    const n = this._newPdfDoc?.numPages ?? 0;
    return Math.max(o, n);
  }

  // ── Render: Scan PDF diff (OCR Enrichment canonical JSON) ───────────────

  private renderScanPdfDiff() {
    const { result, scanLoading, scanError, scanPdfsLoaded, scanZoom, scanPageIdx } = this.state;
    const diff = result?.scanDiff;
    const isTr = this.props.locale === "tr";

    // Kick off PDF loading once per comparison result. scheduleCompare() resets _oldPdfDoc/_newPdfDoc
    // so we check for null here to trigger on each fresh result.
    if (!scanLoading && !scanError && !scanPdfsLoaded) {
      void this.loadScanPdfs();
    }

    if (scanError) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            color: "#c00",
            padding: 24,
            textAlign: "center"
          }}
        >
          {(isTr ? "Belge render edilemedi: " : "Document could not be rendered: ") + scanError}
        </div>
      );
    }

    if (scanLoading || !this._oldPdfDoc || !this._newPdfDoc) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            color: "#666"
          }}
        >
          {isTr ? "Belge PDF'e render ediliyor..." : "Rendering documents to PDF..."}
        </div>
      );
    }

    if (!diff || !diff.pages || diff.pages.length === 0) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            color: "#888"
          }}
        >
          {isTr ? "Tarama karşılaştırması oluşturulamadı." : "Scan comparison could not be generated."}
        </div>
      );
    }

    // Fill tones soft enough to look like subtle underlines rather than color blocks. The
    // backend-side CanonicalDiffEngine now clamps bbox height to a per-word font-size
    // estimate, so rects arrive here already word-sized — we don't need the frontend
    // skip/clamp hack anymore.
    const typeFill: Record<string, string> = {
      unchanged: "transparent",
      added: "rgba(40,167,69,0.08)",
      deleted: "rgba(215,58,73,0.08)",
      modified: "rgba(255,193,7,0.10)",
      moved: "rgba(0,123,255,0.08)"
    };
    const typeStroke: Record<string, string> = {
      unchanged: "#e0e0e0",
      added: "#28a745",
      deleted: "#d73a49",
      modified: "#e36209",
      moved: "#007bff"
    };

    /** Maps a word-level token type (from CanonicalTokenDiff) to the overlay fill/stroke keys.
     *  equal    → no rect (skip)
     *  delete   → deleted
     *  insert   → added
     *  modified → modified  */
    const tokenTypeToLine: Record<string, string | null> = {
      equal: null,
      delete: "deleted",
      insert: "added",
      modified: "modified"
    };
    const typeLabel: Record<string, string> = {
      unchanged: isTr ? "Aynı" : "Unchanged",
      added: isTr ? "Eklendi" : "Added",
      deleted: isTr ? "Silindi" : "Deleted",
      modified: isTr ? "Değişti" : "Modified",
      moved: isTr ? "Taşındı" : "Moved"
    };

    /** Phase 6 v2: confidence-aware decorative region style. Combines side/hamming visuals
     *  (presence-based) with Python's multi-signal confidence score (trust-based). Suspect
     *  regions (confidence in [0.40, 0.70)) get a desaturated stroke + `?` badge so the
     *  user knows to verify them; high-confidence regions get full-strength styling.
     *  Below 0.40 the Python post-filter already drops the region, so we never see it here.
     */
    const SUSPECT_CONFIDENCE_THRESHOLD = 0.7;
    const decorativeStyle = (
      side: "old" | "new" | "both",
      hamming: number | null | undefined,
      confidence: number | null | undefined
    ): { stroke: string; dash?: string; fill: string; label: string; suspect: boolean } => {
      // Suspect band: confidence present AND below high-confidence threshold.
      const suspect = typeof confidence === "number" && confidence < SUSPECT_CONFIDENCE_THRESHOLD;
      if (side === "new") {
        return suspect
          ? { stroke: "#fb923c", dash: "4,3", fill: "rgba(251,146,60,0.05)", label: "+?", suspect: true }
          : { stroke: "#16a34a", dash: "6,4", fill: "rgba(22,163,74,0.06)", label: "+", suspect: false };
      }
      if (side === "old") {
        return suspect
          ? { stroke: "#fb923c", dash: "4,3", fill: "rgba(251,146,60,0.05)", label: "−?", suspect: true }
          : { stroke: "#dc2626", dash: "6,4", fill: "rgba(220,38,38,0.06)", label: "−", suspect: false };
      }
      // side === "both" — visibly changed on both sides.
      if (suspect) return { stroke: "#fb923c", dash: "4,3", fill: "rgba(251,146,60,0.05)", label: "~?", suspect: true };
      if (hamming == null) return { stroke: "#94a3b8", dash: "6,4", fill: "transparent", label: "~?", suspect: false };
      if (hamming <= 15) return { stroke: "#eab308", fill: "rgba(234,179,8,0.06)", label: "~", suspect: false };
      return { stroke: "#ea580c", fill: "rgba(234,88,12,0.08)", label: "≠", suspect: false };
    };

    const decorativeTypeLabel = (type: string): string => {
      const map: Record<string, string> = isTr
        ? {
            signature: "imza",
            stamp: "kaşe",
            handwriting: "el yazısı",
            figure: "şekil",
            chart: "grafik",
            image: "görsel",
            logo: "logo",
            formula: "formül"
          }
        : {
            signature: "signature",
            stamp: "stamp",
            handwriting: "handwriting",
            figure: "figure",
            chart: "chart",
            image: "image",
            logo: "logo",
            formula: "formula"
          };
      return map[type] ?? type;
    };

    const s = diff.summary;
    const pageCount = this.getScanPageCount();
    const pageIdx = Math.max(0, Math.min(scanPageIdx, pageCount - 1));
    const currentPageDiff = diff.pages[pageIdx];
    // Backend always emits oldPageWidthPx / newPageWidthPx (from CanonicalPage.WidthPx) — use
    // it directly. `findCanonicalPageWidth` bbox-envelope fallback produced drift at DPI 300
    // (its hardcoded A4@200=1654 guess was the wrong scale) so it's been removed from the
    // primary path. If a legacy diff omits the field entirely we bail with null, which just
    // hides the overlay for that page rather than drawing in the wrong place.
    const canonicalOldPageWidthPx = currentPageDiff?.oldPageWidthPx ?? null;
    const canonicalNewPageWidthPx = currentPageDiff?.newPageWidthPx ?? null;

    // Build side-specific line lists for the overlay.
    const oldOverlayLines = currentPageDiff?.lines.filter((l) => l.oldBbox) ?? [];
    const newOverlayLines = currentPageDiff?.lines.filter((l) => l.newBbox) ?? [];

    const dpr = Math.max(1, typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1);
    const oldOverlayScale =
      this._scanOldViewport && canonicalOldPageWidthPx
        ? this._scanOldViewport.width / dpr / canonicalOldPageWidthPx
        : 0;
    const newOverlayScale =
      this._scanNewViewport && canonicalNewPageWidthPx
        ? this._scanNewViewport.width / dpr / canonicalNewPageWidthPx
        : 0;

    const canvasDisplayOld = this._scanOldViewport
      ? { w: this._scanOldViewport.width / dpr, h: this._scanOldViewport.height / dpr }
      : null;
    const canvasDisplayNew = this._scanNewViewport
      ? { w: this._scanNewViewport.width / dpr, h: this._scanNewViewport.height / dpr }
      : null;

    return (
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Toolbar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "6px 12px",
            background: "#fafafa",
            borderBottom: "1px solid #e0e0e0",
            fontSize: 12
          }}
        >
          <span style={{ color: "#444" }}>
            <strong>{isTr ? "Tarama" : "Scan"}</strong>
            <span style={{ marginLeft: 6 }}>
              +{s.additions} -{s.deletions} ~{s.modifications}
              {s.movedCount > 0 ? ` ⇅${s.movedCount}` : ""}
            </span>
            <span
              style={{
                marginLeft: 10,
                color:
                  s.similarityPercent < 60
                    ? "#b45309" /* amber-700 — warning */
                    : s.similarityPercent < 80
                      ? "#666"
                      : "#15803d" /* green-700 — confident */,
                fontWeight: s.similarityPercent < 60 ? 600 : 400
              }}
              title={
                s.similarityPercent < 60
                  ? isTr
                    ? "Düşük benzerlik. OCR/metin çıkarımı kalitesi düşük olabilir; sonuçları manuel kontrol etmeniz önerilir."
                    : "Low similarity. OCR / text extraction quality may be poor; manual review recommended."
                  : undefined
              }
            >
              {s.similarityPercent.toFixed(1)}% {isTr ? "benzer" : "similar"}
              {s.similarityPercent < 60 && (
                <span style={{ marginLeft: 4 }} aria-hidden="true">
                  ⚠
                </span>
              )}
            </span>
            {s.uncertainCount > 0 && (
              <span style={{ marginLeft: 10, color: "#6c757d" }}>
                • {s.uncertainCount} {isTr ? "belirsiz" : "uncertain"}
              </span>
            )}
            {(s.decorativeRegionsDelta ?? 0) !== 0 && (
              <span style={{ marginLeft: 10, color: "#0f766e" }}>
                • {isTr ? "görsel bölge" : "regions"} {(s.decorativeRegionsDelta ?? 0) > 0 ? "+" : ""}
                {s.decorativeRegionsDelta}
              </span>
            )}
            {(s.signatureDelta ?? 0) !== 0 && (
              <span style={{ marginLeft: 10, color: "#7c3aed" }}>
                • {isTr ? "imza" : "signatures"} {(s.signatureDelta ?? 0) > 0 ? "+" : ""}
                {s.signatureDelta}
              </span>
            )}
          </span>
          <span style={{ flex: 1 }} />
          {/* Page navigation */}
          <button onClick={() => this.changeScanPage(-1)} disabled={pageIdx === 0} style={btnStyle(pageIdx === 0)}>
            ‹
          </button>
          <span style={{ minWidth: 60, textAlign: "center" }}>
            {isTr ? "Sayfa" : "Page"} {pageIdx + 1} / {pageCount}
          </span>
          <button
            onClick={() => this.changeScanPage(1)}
            disabled={pageIdx >= pageCount - 1}
            style={btnStyle(pageIdx >= pageCount - 1)}
          >
            ›
          </button>
          {/* Zoom */}
          <span style={{ marginLeft: 10 }} />
          <button onClick={() => this.changeScanZoom(-0.15)} style={btnStyle(false)}>
            −
          </button>
          <span style={{ minWidth: 44, textAlign: "center" }}>{Math.round(scanZoom * 100)}%</span>
          <button onClick={() => this.changeScanZoom(0.15)} style={btnStyle(false)}>
            +
          </button>
          {/* Legend */}
          <span style={{ marginLeft: 14, display: "inline-flex", gap: 8, alignItems: "center" }}>
            <LegendDot color={typeStroke.added} label={typeLabel.added} />
            <LegendDot color={typeStroke.deleted} label={typeLabel.deleted} />
            <LegendDot color={typeStroke.modified} label={typeLabel.modified} />
            {s.movedCount > 0 && <LegendDot color={typeStroke.moved} label={typeLabel.moved} />}
          </span>
        </div>

        {/* Side-by-side canvases */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden", background: "#eaeaea" }}>
          <div
            ref={(r) => {
              this._leftPanelRef = r;
            }}
            onScroll={this.onScanLeftScroll}
            style={{
              flex: 1,
              overflow: "auto",
              borderRight: "1px solid #ccc",
              padding: 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start"
            }}
          >
            <div style={{ position: "relative", boxShadow: "0 1px 4px rgba(0,0,0,0.2)", background: "#fff" }}>
              {currentPageDiff?.oldPageNumber == null ? (
                <div style={{ padding: 40, color: "#888", fontStyle: "italic" }}>
                  {isTr ? "Bu sayfa yeni sürümde eklenmiş" : "Page added in the new version"}
                </div>
              ) : (
                <>
                  <canvas ref={this.onScanOldCanvasRef} />
                  {canvasDisplayOld && oldOverlayScale > 0 && (
                    <svg
                      style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
                      width={canvasDisplayOld.w}
                      height={canvasDisplayOld.h}
                    >
                      {oldOverlayLines.map((ln, i) => {
                        if (!ln.oldBbox) return null;
                        if (ln.type === "unchanged") return null;

                        // Per-token rendering only — every changed word gets its own rect
                        // at its own bbox. The previous "denseChange" branch used to drop
                        // the per-token overlay on heavily-edited lines and replace it
                        // with one big line-level block, but that was the opposite of
                        // word-level highlighting: it merged multiple words into a single
                        // colored bar that hid which words actually changed. Backend
                        // already emits accurate per-token bboxes (multi-line paragraphs
                        // are laid out via ComputeGridRenderBbox), so just render them.
                        //
                        // Fallback to a line-level rect ONLY when no tokens were emitted
                        // at all (rare — older diffs without word-level tokens, or lines
                        // where every word had a null bbox after clamp).
                        const tokens = ln.tokens ?? null;
                        const renderableTokens =
                          tokens?.filter((t) => tokenTypeToLine[t.type] != null && t.bbox != null) ?? [];

                        if (renderableTokens.length === 0) {
                          // Defensive: if backend emitted tokens but none of them are
                          // renderable (every token is "equal" or has a null bbox), the
                          // line has no actual visible change on this side. Skip the
                          // line-level fallback rect — basing it would produce a false-
                          // positive overlay over content that is in fact unchanged.
                          // Fallback rect is reserved for the legacy diff format that
                          // doesn't emit tokens at all.
                          if (tokens && tokens.length > 0) return null;
                          const fill = typeFill[ln.type] ?? "transparent";
                          const stroke = typeStroke[ln.type] ?? "#ccc";
                          return (
                            <rect
                              key={`old-${i}`}
                              x={ln.oldBbox.x * oldOverlayScale}
                              y={ln.oldBbox.y * oldOverlayScale}
                              width={ln.oldBbox.w * oldOverlayScale}
                              height={ln.oldBbox.h * oldOverlayScale}
                              fill={fill}
                              stroke={stroke}
                              strokeWidth={0.75}
                              rx={1.5}
                              ry={1.5}
                            />
                          );
                        }

                        return (
                          <g key={`old-${i}`}>
                            {renderableTokens.map((tok, ti) => {
                              const mappedType = tokenTypeToLine[tok.type]!;
                              const fill = typeFill[mappedType] ?? "transparent";
                              const stroke = typeStroke[mappedType] ?? "#ccc";
                              const hasCharDiff = tok.type === "modified" && (tok.charDiff?.length ?? 0) > 0;
                              return (
                                <rect
                                  key={`old-${i}-${ti}`}
                                  x={tok.bbox!.x * oldOverlayScale}
                                  y={tok.bbox!.y * oldOverlayScale}
                                  width={tok.bbox!.w * oldOverlayScale}
                                  height={tok.bbox!.h * oldOverlayScale}
                                  fill={fill}
                                  stroke={stroke}
                                  strokeWidth={0.75}
                                  rx={1.5}
                                  ry={1.5}
                                  style={hasCharDiff ? { pointerEvents: "all", cursor: "help" } : undefined}
                                  onMouseEnter={hasCharDiff ? (e) => this.onModifiedTokenEnter(tok, e) : undefined}
                                  onMouseMove={hasCharDiff ? this.onModifiedTokenMove : undefined}
                                  onMouseLeave={hasCharDiff ? this.onModifiedTokenLeave : undefined}
                                >
                                  {tok.type === "modified" && tok.oldText && tok.text && (
                                    <title>{`${tok.oldText} → ${tok.text}`}</title>
                                  )}
                                </rect>
                              );
                            })}
                          </g>
                        );
                      })}
                      {/* Decorative regions (signature / stamp / handwriting / figure / chart /
                          image / logo / formula). Text diff skips these; this overlay is how
                          they become visible to the user. Shown for side=="old" (removed) and
                          side=="both" (present + hamming-banded). Side=="new" is drawn on the
                          NEW-side canvas only. */}
                      {(currentPageDiff?.decorativeRegions ?? []).map((r, i) => {
                        if (!r.oldBbox) return null; // side=="new" has no oldBbox → skip here
                        // Backend only emits decorative regions that are MISSING on one
                        // side or VISUALLY CHANGED — same-on-both-sides regions are
                        // dropped at PopulateDecorativeRegions. So every region that
                        // reaches us deserves a full-strength marker; no opacity
                        // attenuation, no mega-overlay hiding. Phase 6 v2: suspect-
                        // confidence regions (conf < 0.70) get a desaturated dashed
                        // border + `?` badge so the user knows to verify.
                        const st = decorativeStyle(r.side, r.pHashHammingDistance, r.confidence);
                        const label = `${decorativeTypeLabel(r.type)} ${st.label}`;
                        const confPct = typeof r.confidence === "number" ? `${Math.round(r.confidence * 100)}%` : null;
                        const tooltip = [
                          label,
                          confPct != null ? `${isTr ? "Güven" : "Confidence"}: ${confPct}` : null,
                          r.pHashHammingDistance != null ? `pHash Δ=${r.pHashHammingDistance}` : null,
                          st.suspect ? (isTr ? "Lütfen kontrol edin" : "Please verify") : null
                        ]
                          .filter(Boolean)
                          .join(" · ");
                        return (
                          <g key={`old-dec-${i}`}>
                            <rect
                              x={r.oldBbox.x * oldOverlayScale}
                              y={r.oldBbox.y * oldOverlayScale}
                              width={r.oldBbox.w * oldOverlayScale}
                              height={r.oldBbox.h * oldOverlayScale}
                              fill={st.fill}
                              stroke={st.stroke}
                              strokeWidth={2}
                              strokeDasharray={st.dash}
                              rx={2}
                              ry={2}
                            >
                              <title>{tooltip}</title>
                            </rect>
                            {st.suspect && (
                              <g pointerEvents="none">
                                <circle
                                  cx={r.oldBbox.x * oldOverlayScale + r.oldBbox.w * oldOverlayScale - 10}
                                  cy={r.oldBbox.y * oldOverlayScale + 10}
                                  r={9}
                                  fill="#fb923c"
                                  stroke="#fff"
                                  strokeWidth={1.5}
                                />
                                <text
                                  x={r.oldBbox.x * oldOverlayScale + r.oldBbox.w * oldOverlayScale - 10}
                                  y={r.oldBbox.y * oldOverlayScale + 14}
                                  fontSize={12}
                                  fontWeight="bold"
                                  fill="#fff"
                                  textAnchor="middle"
                                >
                                  ?
                                </text>
                              </g>
                            )}
                          </g>
                        );
                      })}
                    </svg>
                  )}
                </>
              )}
            </div>
          </div>
          <div
            ref={(r) => {
              this._rightPanelRef = r;
            }}
            onScroll={this.onScanRightScroll}
            style={{
              flex: 1,
              overflow: "auto",
              padding: 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start"
            }}
          >
            <div style={{ position: "relative", boxShadow: "0 1px 4px rgba(0,0,0,0.2)", background: "#fff" }}>
              {currentPageDiff?.newPageNumber == null ? (
                <div style={{ padding: 40, color: "#888", fontStyle: "italic" }}>
                  {isTr ? "Bu sayfa yeni sürümde silinmiş" : "Page removed in the new version"}
                </div>
              ) : (
                <>
                  <canvas ref={this.onScanNewCanvasRef} />
                  {canvasDisplayNew && newOverlayScale > 0 && (
                    <svg
                      style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
                      width={canvasDisplayNew.w}
                      height={canvasDisplayNew.h}
                    >
                      {newOverlayLines.map((ln, i) => {
                        if (!ln.newBbox) return null;
                        if (ln.type === "unchanged") return null;

                        // Mirror of OLD side — per-token only, no denseChange skip.
                        const tokens = ln.tokens ?? null;
                        const renderableTokens =
                          tokens?.filter((t) => tokenTypeToLine[t.type] != null && t.newBbox != null) ?? [];

                        if (renderableTokens.length === 0) {
                          // Defensive: if backend emitted tokens but none of them are
                          // renderable (every token is "equal" or has a null bbox), the
                          // line has no actual visible change on this side. Skip the
                          // line-level fallback rect — basing it would produce a false-
                          // positive overlay over content that is in fact unchanged.
                          // Fallback rect is reserved for the legacy diff format that
                          // doesn't emit tokens at all.
                          if (tokens && tokens.length > 0) return null;
                          const fill = typeFill[ln.type] ?? "transparent";
                          const stroke = typeStroke[ln.type] ?? "#ccc";
                          return (
                            <rect
                              key={`new-${i}`}
                              x={ln.newBbox.x * newOverlayScale}
                              y={ln.newBbox.y * newOverlayScale}
                              width={ln.newBbox.w * newOverlayScale}
                              height={ln.newBbox.h * newOverlayScale}
                              fill={fill}
                              stroke={stroke}
                              strokeWidth={0.75}
                              rx={1.5}
                              ry={1.5}
                            />
                          );
                        }

                        return (
                          <g key={`new-${i}`}>
                            {renderableTokens.map((tok, ti) => {
                              const mappedType = tokenTypeToLine[tok.type]!;
                              const fill = typeFill[mappedType] ?? "transparent";
                              const stroke = typeStroke[mappedType] ?? "#ccc";
                              const hasCharDiff = tok.type === "modified" && (tok.charDiff?.length ?? 0) > 0;
                              return (
                                <rect
                                  key={`new-${i}-${ti}`}
                                  x={tok.newBbox!.x * newOverlayScale}
                                  y={tok.newBbox!.y * newOverlayScale}
                                  width={tok.newBbox!.w * newOverlayScale}
                                  height={tok.newBbox!.h * newOverlayScale}
                                  fill={fill}
                                  stroke={stroke}
                                  strokeWidth={0.75}
                                  rx={1.5}
                                  ry={1.5}
                                  style={hasCharDiff ? { pointerEvents: "all", cursor: "help" } : undefined}
                                  onMouseEnter={hasCharDiff ? (e) => this.onModifiedTokenEnter(tok, e) : undefined}
                                  onMouseMove={hasCharDiff ? this.onModifiedTokenMove : undefined}
                                  onMouseLeave={hasCharDiff ? this.onModifiedTokenLeave : undefined}
                                >
                                  {tok.type === "modified" && tok.oldText && tok.text && (
                                    <title>{`${tok.oldText} → ${tok.text}`}</title>
                                  )}
                                </rect>
                              );
                            })}
                          </g>
                        );
                      })}
                      {/* Decorative regions on the NEW side — mirrors the OLD-side block.
                          Backend filters same-on-both-sides regions out; everything that
                          arrives here deserves a full-strength marker. Suspect-confidence
                          regions get a `?` badge so the user can verify. */}
                      {(currentPageDiff?.decorativeRegions ?? []).map((r, i) => {
                        if (!r.newBbox) return null; // side=="old" has no newBbox → skip here
                        const st = decorativeStyle(r.side, r.pHashHammingDistance, r.confidence);
                        const label = `${decorativeTypeLabel(r.type)} ${st.label}`;
                        const confPct = typeof r.confidence === "number" ? `${Math.round(r.confidence * 100)}%` : null;
                        const tooltip = [
                          label,
                          confPct != null ? `${isTr ? "Güven" : "Confidence"}: ${confPct}` : null,
                          r.pHashHammingDistance != null ? `pHash Δ=${r.pHashHammingDistance}` : null,
                          st.suspect ? (isTr ? "Lütfen kontrol edin" : "Please verify") : null
                        ]
                          .filter(Boolean)
                          .join(" · ");
                        return (
                          <g key={`new-dec-${i}`}>
                            <rect
                              x={r.newBbox.x * newOverlayScale}
                              y={r.newBbox.y * newOverlayScale}
                              width={r.newBbox.w * newOverlayScale}
                              height={r.newBbox.h * newOverlayScale}
                              fill={st.fill}
                              stroke={st.stroke}
                              strokeWidth={2}
                              strokeDasharray={st.dash}
                              rx={2}
                              ry={2}
                            >
                              <title>{tooltip}</title>
                            </rect>
                            {st.suspect && (
                              <g pointerEvents="none">
                                <circle
                                  cx={r.newBbox.x * newOverlayScale + r.newBbox.w * newOverlayScale - 10}
                                  cy={r.newBbox.y * newOverlayScale + 10}
                                  r={9}
                                  fill="#fb923c"
                                  stroke="#fff"
                                  strokeWidth={1.5}
                                />
                                <text
                                  x={r.newBbox.x * newOverlayScale + r.newBbox.w * newOverlayScale - 10}
                                  y={r.newBbox.y * newOverlayScale + 14}
                                  fontSize={12}
                                  fontWeight="bold"
                                  fill="#fff"
                                  textAnchor="middle"
                                >
                                  ?
                                </text>
                              </g>
                            )}
                          </g>
                        );
                      })}
                    </svg>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* FAZ 6B — char-level diff popover. Renders when the user hovers a "modified"
            token rect that has charDiff segments. Fixed-position so it tracks the cursor
            across both panels and isn't clipped by the canvas containers. pointer-events
            are disabled so the popover itself doesn't trigger mouseleave on the rect. */}
        {this.state.hoveredCharDiff && this.renderCharDiffPopover()}
      </div>
    );
  }

  /** FAZ 6B — render the per-character delete/equal/insert breakdown for a hovered
   *  modified token. Equal segments stay normal-weight; deleted characters are red with
   *  strikethrough; inserted characters are green and bold. Two rows shown: the source
   *  word with its deletions, and the target word with its insertions. */
  private renderCharDiffPopover() {
    const hover = this.state.hoveredCharDiff;
    if (!hover) return null;
    const segments = hover.tok.charDiff ?? [];
    if (segments.length === 0) return null;
    const isTr = (this.props.locale ?? "tr") === "tr";

    // Build two side-by-side renderings: "old" with deletes shown, "new" with inserts.
    const oldRendered = segments
      .filter((s) => s.type === "equal" || s.type === "delete")
      .map((s, i) => {
        if (s.type === "delete") {
          return (
            <span key={i} style={{ color: "#dc2626", textDecoration: "line-through", fontWeight: 600 }}>
              {s.text}
            </span>
          );
        }
        return <span key={i}>{s.text}</span>;
      });

    const newRendered = segments
      .filter((s) => s.type === "equal" || s.type === "insert")
      .map((s, i) => {
        if (s.type === "insert") {
          return (
            <span key={i} style={{ color: "#16a34a", fontWeight: 700, backgroundColor: "rgba(22,163,74,0.12)" }}>
              {s.text}
            </span>
          );
        }
        return <span key={i}>{s.text}</span>;
      });

    // Position the popover slightly down-and-right of the cursor, but flip to up-and-left
    // when it would overflow the viewport. 280px width estimate + 96px height estimate
    // is enough headroom for typical word-pair lengths.
    const PAD = 12;
    const W_EST = 280;
    const H_EST = 96;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    let left = hover.x + PAD;
    let top = hover.y + PAD;
    if (left + W_EST > vw) left = hover.x - W_EST - PAD;
    if (top + H_EST > vh) top = hover.y - H_EST - PAD;

    return (
      <div
        style={{
          position: "fixed",
          left,
          top,
          zIndex: 9999,
          pointerEvents: "none",
          background: "#ffffff",
          border: "1px solid #d1d5db",
          borderRadius: 6,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          padding: "8px 12px",
          fontFamily: "system-ui, -apple-system, sans-serif",
          fontSize: 13,
          lineHeight: 1.4,
          maxWidth: W_EST
        }}
      >
        <div
          style={{ fontSize: 10, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}
        >
          {isTr ? "Karakter Farkı" : "Character diff"}
        </div>
        <div style={{ marginBottom: 2 }}>
          <span style={{ fontSize: 10, color: "#9ca3af", marginRight: 6 }}>{isTr ? "Eski" : "Old"}</span>
          {oldRendered}
        </div>
        <div>
          <span style={{ fontSize: 10, color: "#9ca3af", marginRight: 6 }}>{isTr ? "Yeni" : "New"}</span>
          {newRendered}
        </div>
      </div>
    );
  }

  // Canonical page'in pixel genişliği — overlay koordinatlarını canvas coord'una dönüştürmek için
  // bbox scale hesaplaması.
  private findCanonicalPageWidth(pageNumber: number, _side: "old" | "new"): number | null {
    const diff = this.state.result?.scanDiff;
    if (!diff) return null;
    // Canonical JSON page widths are not carried by the diff shape itself — they live on the original
    // CanonicalDocument. As a pragmatic fallback we derive per-page scale from the bbox envelope:
    // pick the maximum right edge across all lines on this page-side. If no lines are present,
    // default to pdfjs viewport width (no rescaling).
    const page = diff.pages.find(
      (p) => (_side === "old" && p.oldPageNumber === pageNumber) || (_side === "new" && p.newPageNumber === pageNumber)
    );
    if (!page) return null;
    let maxRight = 0;
    for (const ln of page.lines) {
      const b = _side === "old" ? ln.oldBbox : ln.newBbox;
      if (!b) continue;
      const right = b.x + b.w;
      if (right > maxRight) maxRight = right;
    }
    // A4 @ 200 dpi ≈ 1654px wide. Use whichever is larger — this keeps scale sane if all bboxes
    // are far left of the page margin.
    return Math.max(maxRight, 1654);
  }

  // ── Render: Slide diff (PPTX) ────────────────────────────────────────────

  private renderSlideDiff() {
    const { result } = this.state;
    const slides: any[] = (result as any)?.slideChanges ?? [];
    const isTr = this.props.locale === "tr";

    if (slides.length === 0) return this.renderIdentical();

    const typeColor: Record<string, string> = { added: "#e6ffed", deleted: "#ffeef0", modified: "#fff8e1" };
    const typeBadge: Record<string, string> = { added: "#28a745", deleted: "#d73a49", modified: "#e36209" };
    const typeLabel: Record<string, string> = {
      added: isTr ? "Yeni Slayt" : "New Slide",
      deleted: isTr ? "Silinen Slayt" : "Deleted Slide",
      modified: isTr ? "Değişen Slayt" : "Modified Slide"
    };

    const lineStyle = (type: string): CSSProperties => ({
      padding: "1px 6px",
      fontFamily: "monospace",
      fontSize: 11,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      minHeight: 18,
      lineHeight: "18px",
      background: type === "added" ? "#e6ffed" : type === "deleted" ? "#ffeef0" : "transparent",
      borderLeft:
        type === "added" ? "2px solid #28a745" : type === "deleted" ? "2px solid #d73a49" : "2px solid transparent"
    });

    return (
      <div style={{ width: "100%", height: "100%", overflow: "auto", fontFamily: "sans-serif", fontSize: 12 }}>
        {slides.map((s: any, i: number) => (
          <div key={i} style={{ marginBottom: 8, border: "1px solid #e0e0e0", borderRadius: 8, overflow: "hidden" }}>
            {/* Slide header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                background: typeColor[s.type] || "#f8f9fa",
                borderBottom: "1px solid #e0e0e0"
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 13 }}>
                {isTr ? "Slayt" : "Slide"} {s.slideNumber}
              </span>
              <span
                style={{
                  display: "inline-block",
                  padding: "1px 8px",
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#fff",
                  background: typeBadge[s.type] || "#888"
                }}
              >
                {typeLabel[s.type] || s.type}
              </span>
            </div>

            {/* Slide content diff */}
            {s.type === "modified" && s.chunks?.length > 0 ? (
              <div style={{ maxHeight: 200, overflow: "auto", padding: 4 }}>
                {s.chunks.map((chunk: any, ci: number) => (
                  <div key={ci} style={lineStyle(chunk.type)}>
                    {chunk.type === "deleted" ? chunk.oldContent : chunk.content}
                  </div>
                ))}
              </div>
            ) : s.type === "added" ? (
              <div style={{ padding: "8px 12px", fontSize: 11, color: "#28a745" }}>{s.newText}</div>
            ) : s.type === "deleted" ? (
              <div style={{ padding: "8px 12px", fontSize: 11, color: "#d73a49", textDecoration: "line-through" }}>
                {s.oldText}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    );
  }

  // ── Render: Cell diff (XLSX) ─────────────────────────────────────────────

  private renderCellDiff() {
    const { result } = this.state;
    const changes: any[] = (result as any)?.cellChanges ?? [];
    const isTr = this.props.locale === "tr";

    if (changes.length === 0) {
      return this.renderIdentical();
    }

    const typeColor: Record<string, string> = {
      added: "#e6ffed",
      deleted: "#ffeef0",
      modified: "#fff8e1"
    };
    const typeLabel: Record<string, string> = {
      added: isTr ? "Eklenen" : "Added",
      deleted: isTr ? "Silinen" : "Deleted",
      modified: isTr ? "Değişen" : "Modified"
    };
    const typeBadge: Record<string, string> = {
      added: "#28a745",
      deleted: "#d73a49",
      modified: "#e36209"
    };

    return (
      <div style={{ width: "100%", height: "100%", overflow: "auto", fontFamily: "sans-serif", fontSize: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8f9fa", position: "sticky", top: 0, zIndex: 1 }}>
              <th
                style={{
                  padding: "8px 12px",
                  textAlign: "left",
                  borderBottom: "2px solid #dee2e6",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#495057"
                }}
              >
                {isTr ? "Hücre" : "Cell"}
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  textAlign: "left",
                  borderBottom: "2px solid #dee2e6",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#495057"
                }}
              >
                {isTr ? "Durum" : "Status"}
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  textAlign: "left",
                  borderBottom: "2px solid #dee2e6",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#495057"
                }}
              >
                {isTr ? "Eski Değer" : "Old Value"}
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  textAlign: "left",
                  borderBottom: "2px solid #dee2e6",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#495057"
                }}
              >
                {isTr ? "Yeni Değer" : "New Value"}
              </th>
            </tr>
          </thead>
          <tbody>
            {changes.map((c: any, i: number) => (
              <tr key={i} style={{ background: typeColor[c.type] || "transparent" }}>
                <td
                  style={{
                    padding: "6px 12px",
                    borderBottom: "1px solid #eee",
                    fontFamily: "monospace",
                    fontWeight: 600
                  }}
                >
                  {c.cell}
                </td>
                <td style={{ padding: "6px 12px", borderBottom: "1px solid #eee" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "1px 8px",
                      borderRadius: 10,
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#fff",
                      background: typeBadge[c.type] || "#888"
                    }}
                  >
                    {typeLabel[c.type] || c.type}
                  </span>
                </td>
                <td
                  style={{
                    padding: "6px 12px",
                    borderBottom: "1px solid #eee",
                    color: c.type === "deleted" || c.type === "modified" ? "#d73a49" : "#888",
                    textDecoration: c.type === "modified" ? "line-through" : "none"
                  }}
                >
                  {c.oldValue ?? "—"}
                </td>
                <td
                  style={{
                    padding: "6px 12px",
                    borderBottom: "1px solid #eee",
                    color: c.type === "added" || c.type === "modified" ? "#28a745" : "#888",
                    fontWeight: c.type === "modified" ? 600 : 400
                  }}
                >
                  {c.newValue ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ── Render: Image diff ────────────────────────────────────────────────────

  private renderImageDiff() {
    const { result, oldImageBlobUrl, newImageBlobUrl } = this.state;
    const isTr = this.props.locale === "tr";
    const img = result?.imageDiff;

    if (!img) return this.renderUnsupported();

    const similarity = img.similarityPercent;
    // Color: green if similar, yellow if moderate, red if very different
    const barColor = similarity >= 90 ? "#28a745" : similarity >= 50 ? "#ff9800" : "#d73a49";

    return (
      <div style={{ width: "100%", height: "100%", overflow: "auto", fontFamily: "sans-serif" }}>
        {/* Similarity bar */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e0e0e0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{isTr ? "Benzerlik" : "Similarity"}</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: barColor }}>{similarity.toFixed(1)}%</span>
          </div>
          <div
            style={{
              width: "100%",
              height: 8,
              background: "#eee",
              borderRadius: 4,
              overflow: "hidden"
            }}
          >
            <div
              style={{
                width: `${Math.min(100, similarity)}%`,
                height: "100%",
                background: barColor,
                borderRadius: 4,
                transition: "width 0.5s ease"
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 24, marginTop: 8, fontSize: 11, color: "#888" }}>
            <span>
              {isTr ? "Eski" : "Old"}: {img.oldWidth} x {img.oldHeight}px
            </span>
            <span>
              {isTr ? "Yeni" : "New"}: {img.newWidth} x {img.newHeight}px
            </span>
            {!img.dimensionsMatch && (
              <span style={{ color: "#e36209" }}>{isTr ? "Boyut farklı" : "Dimensions differ"}</span>
            )}
          </div>
        </div>

        {/* Side-by-side image preview */}
        <div style={{ display: "flex", width: "100%", flex: 1 }}>
          <div style={{ flex: 1, padding: 12, borderRight: "1px solid #e0e0e0", textAlign: "center" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8 }}>
              {isTr ? "Eski Versiyon" : "Old Version"} (v{result!.oldVersion})
            </div>
            {oldImageBlobUrl ? (
              <img
                src={oldImageBlobUrl}
                alt="Old version"
                style={{ maxWidth: "100%", maxHeight: 400, border: "1px solid #ddd", borderRadius: 4 }}
              />
            ) : (
              <div style={{ padding: 40, color: "#aaa", fontSize: 12 }}>{isTr ? "Yükleniyor..." : "Loading..."}</div>
            )}
          </div>
          <div style={{ flex: 1, padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8 }}>
              {isTr ? "Yeni Versiyon" : "New Version"} (v{result!.newVersion})
            </div>
            {newImageBlobUrl ? (
              <img
                src={newImageBlobUrl}
                alt="New version"
                style={{ maxWidth: "100%", maxHeight: 400, border: "1px solid #ddd", borderRadius: 4 }}
              />
            ) : (
              <div style={{ padding: 40, color: "#aaa", fontSize: 12 }}>{isTr ? "Yükleniyor..." : "Loading..."}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Error (decryption failure, etc.) ───────────────────────────────

  private renderError() {
    const { result } = this.state;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          color: "#c00",
          fontFamily: "sans-serif"
        }}
      >
        <span style={{ fontSize: 48, marginBottom: 16 }}>&#9888;</span>
        <span style={{ fontSize: 15, fontWeight: 600 }}>
          {this.props.locale === "tr" ? "Karşılaştırma hatası" : "Comparison error"}
        </span>
        <span style={{ fontSize: 13, color: "#888", marginTop: 8, textAlign: "center", maxWidth: 400 }}>
          {result?.message ?? (this.props.locale === "tr" ? "Bilinmeyen hata." : "Unknown error.")}
        </span>
      </div>
    );
  }

  // ── Render: Unsupported ───────────────────────────────────────────────────

  private renderUnsupported() {
    const { result } = this.state;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          color: "#888",
          fontFamily: "sans-serif"
        }}
      >
        <span style={{ fontSize: 48, marginBottom: 16 }}>&#128196;</span>
        <span style={{ fontSize: 15 }}>
          {result?.message ??
            (this.props.locale === "tr"
              ? "Bu format için karşılaştırma desteklenmiyor."
              : "Comparison not available for this format.")}
        </span>
        <span style={{ fontSize: 13, color: "#aaa", marginTop: 8 }}>{result?.mimeType}</span>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────

  render(): ReactNode {
    const { style } = this.props;
    const { loading, error, result } = this.state;
    const leftVersionId = this._overrideLeftVersionId || this.props.leftVersionId;
    const rightVersionId = this._overrideRightVersionId || this.props.rightVersionId;

    if ((window as any)?.kuika?.isDesignTime) {
      return this.renderPlaceholder();
    }

    if (loading) {
      return (
        <div
          className={this.props.className}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            fontFamily: "sans-serif",
            ...style
          }}
        >
          <span>{this.props.locale === "tr" ? "Versiyonlar karşılaştırılıyor..." : "Comparing versions..."}</span>
        </div>
      );
    }

    if (error) {
      return (
        <div
          className={this.props.className}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            color: "#c00",
            fontFamily: "sans-serif",
            ...style
          }}
        >
          <span>
            {this.props.locale === "tr" ? "Karşılaştırma hatası: " : "Comparison error: "}
            {error}
          </span>
        </div>
      );
    }

    if (!result) return this.renderPlaceholder();

    return (
      <div
        className={this.props.className}
        style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 800, ...style }}
      >
        {this.renderSummary()}
        <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>
          <div style={{ flex: 1, overflow: "hidden" }}>
            {result.comparisonMode === "identical"
              ? this.renderIdentical()
              : result.comparisonMode === "error"
                ? this.renderError()
                : result.comparisonMode === "imageDiff"
                  ? this.renderImageDiff()
                  : result.comparisonMode === "pageDiff"
                    ? this.renderPageDiff()
                    : result.comparisonMode === "pdfVisualDiff"
                      ? this.renderPdfVisualDiff()
                      : result.comparisonMode === "scanPdfDiff"
                        ? this.renderScanPdfDiff()
                        : result.comparisonMode === "cellDiff"
                          ? this.renderCellDiff()
                          : result.comparisonMode === "slideDiff"
                            ? this.renderSlideDiff()
                            : result.comparisonMode === "collabora"
                              ? this.state.settings.collabora.enabled
                                ? this.renderCollabora()
                                : this.renderUnsupported()
                              : result.comparisonMode === "docxExport"
                                ? this.renderDocxExportCompare()
                                : result.comparisonMode === "textDiff"
                                  ? this.renderTextDiff()
                                  : this.renderUnsupported()}
          </div>
        </div>
      </div>
    );
  }
}

const DocumentCompare = withCommonEvents(DocumentCompareInner);
export { DocumentCompare };
